import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BugReport,
  CheckOutlined,
  ContentCopyOutlined,
  LockOutlined,
  Description,
  History,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Login,
  Logout,
  DarkModeOutlined,
  FolderOutlined,
  LightModeOutlined,
  MoreHoriz,
  NotificationsNone,
  Search,
  Settings as SettingsIcon,
  SupportAgent,
  TaskAlt,
  Timer,
} from "@mui/icons-material";
import { DocumentationView } from "./components/workspace/documentation/DocumentationView";
import { FunctionalWorkspaceView } from "./components/workspace/functional/FunctionalWorkspaceView";
import { WorkspaceInlineLoader } from "./components/workspace/common/WorkspaceInlineLoader";
import { WorkspaceLanding } from "./components/workspace/public/WorkspaceLanding";
import { LYRICLIOUS_THEME, closeWorkspaceHelp, documentationId, documentationItems, generalItems, getTodayValue, moduleTabs, navItems } from "./data/workspaceConfig";
import {
  createWorkspaceRecord,
  clearWorkspaceSsoReturn,
  captureWorkspaceOnboardingLead,
  deleteWorkspaceRecord,
  getWorkspaceAdministration,
  getWorkspaceBootstrap,
  getWorkspacePeople,
  getRememberedWorkspaceSlug,
  getWorkspaceSlugFromPath,
  heartbeatWorkspaceAttendance,
  isWorkspaceSsoReturn,
  logoutWorkspaceSession,
  rememberWorkspaceSlug,
  trackWorkspacePageVisit,
  submitWorkspaceReport,
  updateWorkspaceAccountPreferences,
  updateWorkspaceAdministration,
  updateWorkspaceRecord,
} from "./services/workspaceApi";

const DEFAULT_WORKSPACE_ICON = "/icons/lyriclious-icon.png";
const DEFAULT_WORKSPACE_WORDMARK = "/icons/lyriclious-wordmark.png";
const CLIENT_SPACE_MODULES = new Set(["Client Admin", "Client Payments", "Client Marketing"]);
const PROJECT_INDEPENDENT_MODULES = new Set(["Home", "My Team"]);
const PRIVATE_OWNER_MODULES = new Set(["Knowledge"]);
const PRIVATE_OWNER_MODULE_EMAILS = new Set(["nishant.nksingh@icloud.com"]);
const normalizeWorkspaceEmail = (value) => `${value || ""}`.trim().toLowerCase();
const isLiveLyricliousWorkspace = () =>
  typeof window !== "undefined" && /lyriclious\.com$/i.test(window.location.hostname);
const getLyricliousPublicUrl = (path) => {
  const publicOrigin =
    isLiveLyricliousWorkspace()
      ? "https://www.lyriclious.com"
      : process.env.REACT_APP_LYRICLIOUS_URL || "http://localhost:3000";
  return `${publicOrigin}${path}`;
};
const getWorkspaceAuthReturnUrl = () => {
  if (typeof window === "undefined") return "";
  const workspaceUrl = new URL(window.location.href);
  workspaceUrl.search = "";
  workspaceUrl.searchParams.set("workspace-login", "1");
  workspaceUrl.hash = "";
  return workspaceUrl.toString();
};
const getLyricliousLoginUrl = () => {
  const loginUrl =
    isLiveLyricliousWorkspace()
      ? getLyricliousPublicUrl("/login")
      : process.env.REACT_APP_LYRICLIOUS_LOGIN_URL || getLyricliousPublicUrl("/login");
  const returnUrl = getWorkspaceAuthReturnUrl();

  if (typeof window !== "undefined") {
    window.sessionStorage.setItem("lyriclious-workspace-login-return", "1");
  }

  return returnUrl ? `${loginUrl}?returnUrl=${encodeURIComponent(returnUrl)}` : loginUrl;
};
const getLyricliousRegisterUrl = () => {
  const registerUrl = getLyricliousPublicUrl("/register");
  const returnUrl = getWorkspaceAuthReturnUrl();
  const params = new URLSearchParams({ source: "workspace" });
  if (returnUrl) params.set("returnUrl", returnUrl);
  return `${registerUrl}?${params.toString()}`;
};
const getSystemWorkspaceAppearance = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};
const resolveWorkspaceAppearance = (mode) => (mode === "system" ? getSystemWorkspaceAppearance() : mode === "dark" ? "dark" : "light");
const workspaceAppearanceStorageKey = "lyriclious-workspace-user-appearance";
const WORKSPACE_GA_MEASUREMENT_ID = "G-X6DR29GZ0N";
const isWorkspaceProduction = () => typeof window !== "undefined" && window.location.hostname === "workspace.lyriclious.com";
const toRouteSegment = (value) => `${value || ""}`.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const moduleFromRouteSegment = (segment) => Object.keys(moduleTabs).find((moduleName) => toRouteSegment(moduleName) === segment) || "";
const isTicketRouteSegment = (segment) => /^[a-z][a-z0-9-]*-\d+$/i.test(`${segment || ""}`) || /^[a-f0-9]{24}$/i.test(`${segment || ""}`);
const getRecordRouteSegment = (record) => record?.ticketSequence && record?.key ? toRouteSegment(record.key) : `${record?.id || ""}`;
const getProjectBrandingIcon = (branding = {}) =>
  branding.icon_url || branding.logo_square_url || branding.logo_url || branding.favicon_ico_url || branding.favicon_url || "";
const isPlaceholderProjectTitle = (value) => /^project\s*\d+$/i.test(`${value || ""}`.trim());
const humanizeRouteLabel = (value) => `${value || ""}`
  .trim()
  .replace(/[-_]+/g, " ")
  .replace(/\s+/g, " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());
const getProjectDisplayTitle = (record = {}) => {
  if (record.displayTitle) return record.displayTitle;
  return isPlaceholderProjectTitle(record.title) && record.projectSlug
    ? humanizeRouteLabel(record.projectSlug)
    : record.title || humanizeRouteLabel(record.projectSlug);
};
const getProjectMatchValues = (record = {}) => [
  record.id,
  record._id,
  record.title,
  record.displayTitle,
  record.projectSlug,
  record.projectRef,
  record.key,
].map((value) => `${value || ""}`.trim().toLowerCase()).filter(Boolean);
const getProjectRouteSegment = (record, records = []) => {
  const recordProjectValues = [record?.projectId, record?.projectRef, record?.projectSlug]
    .map((value) => `${value || ""}`.trim().toLowerCase())
    .filter(Boolean);
  const project = records.find((item) =>
    item?.moduleName === "Projects"
    && (
      item.id === record?.projectId
      || (record?.moduleName === "Projects" && item.id === record?.id)
      || getProjectMatchValues(item).some((value) => recordProjectValues.includes(value))
    )
  );
  return project?.projectSlug || record?.projectSlug || "";
};
const recordMatchesProjectRoute = (record, routeProjectId, records = []) => {
  if (!routeProjectId) return true;
  const normalizedRoute = `${routeProjectId || ""}`.trim().toLowerCase();
  const projectRoute = getProjectRouteSegment(record, records);
  if (projectRoute && `${projectRoute}`.trim().toLowerCase() === normalizedRoute) return true;
  const recordProjectValues = [record?.projectId, record?.projectRef, record?.projectSlug]
    .map((value) => `${value || ""}`.trim().toLowerCase())
    .filter(Boolean);
  if (recordProjectValues.includes(normalizedRoute)) return true;
  const routedProject = records.find((item) => item?.moduleName === "Projects" && getProjectMatchValues(item).includes(normalizedRoute));
  if (!routedProject) return false;
  return getProjectMatchValues(routedProject).some((value) => recordProjectValues.includes(value));
};
const isRoutableTicketRecord = (record) =>
  Boolean(record) && record.moduleName === "Work" && (
    ["Tickets", "Backlog"].includes(record.tab || record.tabName)
    || Boolean(record.ticketSequence)
    || Boolean(record.ticketPrefix)
    || (/task|ticket|bug|request|support|incident|feature|story|improvement/i.test(`${record.type || ""}`) && !/epic|sprint|template|automation|approval/i.test(`${record.type || ""}`))
  );
const getSavedWorkspaceAppearance = () => {
  if (typeof window === "undefined") return "light";
  const value = window.localStorage.getItem(workspaceAppearanceStorageKey);
  return ["light", "dark", "system"].includes(value) ? value : "light";
};
const STATIC_RUNTIME_MODULES = navItems.map((item) => ({
  key: item.label,
  label: item.label,
  status: "live",
  enabled: !PRIVATE_OWNER_MODULES.has(item.label),
  premium: false,
  comingSoon: item.comingSoon === true,
  order: navItems.findIndex((entry) => entry.label === item.label) + 1,
  tabs: (moduleTabs[item.label] || []).map((tab, index) => ({
    key: `${item.label}-${tab}-${index}`,
    name: tab,
    label: tab,
    enabled: true,
    premium: false,
    order: index + 1,
  })),
}));

const normalizeModuleTabsForRuntime = (tabs = []) =>
  (Array.isArray(tabs) ? tabs : [])
    .map((tab, index) => {
      if (typeof tab === "string") {
        return {
          key: `tab-${tab}-${index}`,
          name: tab,
          label: tab,
          enabled: true,
          premium: false,
          order: index + 1,
        };
      }
      const name = tab?.name || tab?.label || "";
      if (!name) return null;
      return {
        key: tab.key || `tab-${name}-${index}`,
        name,
        label: tab.label || name,
        enabled: tab.enabled !== false,
        premium: tab.premium === true,
        order: Number.isFinite(Number(tab.order)) ? Number(tab.order) : index + 1,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.order - b.order);

const mergeRuntimeModules = (storedModules = []) => {
  const storedMap = new Map((storedModules || []).map((module) => [module.key, module]));
  const defaultModules = STATIC_RUNTIME_MODULES.map((defaultModule) => {
    const stored = storedMap.get(defaultModule.key);
    if (!stored) return defaultModule;
    const storedTabs = normalizeModuleTabsForRuntime(stored.tabs || []);
    const defaultTabs = defaultModule.tabs || [];
    const mergedTabs = defaultTabs.map((defaultTab) =>
      storedTabs.find((tab) => tab.name === defaultTab.name) || defaultTab
    );
    storedTabs
      .filter((tab) => !defaultTabs.some((defaultTab) => defaultTab.name === tab.name))
      .forEach((tab) => mergedTabs.push(tab));
    return {
      ...defaultModule,
      ...stored,
      enabled: stored.enabled !== false,
      premium: stored.premium === true,
      comingSoon: defaultModule.comingSoon === true,
      tabs: mergedTabs,
    };
  });
  const additionalModules = (storedModules || [])
    .filter((module) => module?.key && !STATIC_RUNTIME_MODULES.some((defaultModule) => defaultModule.key === module.key))
    .map((module, index) => ({
      ...module,
      label: module.label || module.key,
      enabled: module.enabled !== false,
      premium: module.premium === true,
      tabs: normalizeModuleTabsForRuntime(module.tabs || []),
      order: Number.isFinite(Number(module.order)) ? Number(module.order) : STATIC_RUNTIME_MODULES.length + index + 1,
    }));
  return [...defaultModules, ...additionalModules].sort((left, right) => (left.order || 0) - (right.order || 0));
};

const getWorkspaceUrlState = () => {
  if (typeof window === "undefined") {
    return {
      activeNav: "Home",
      documentationLabel: "Product Blueprint",
      documentationMode: false,
      selectedProject: "",
      tab: moduleTabs.Home?.[0] || "Overview",
      activeBoardId: "",
    };
  }

  const params = new URLSearchParams(window.location.search);
  const pathSegments = window.location.pathname.split("/").filter(Boolean);
  const workspaceSlug = pathSegments[0] || "";
  const projectSegment = pathSegments[1] || "";
  const routedModule = moduleFromRouteSegment(pathSegments[2] || "");
  const routedTab = pathSegments[3] || "";
  const ticketSegment = pathSegments[4] || "";
  const rootModule = moduleFromRouteSegment(pathSegments[1] || "");
  const rootTab = pathSegments[2] || "";
  const shortTicketSegment = pathSegments[2] || "";
  const isShortTicketRoute = Boolean(workspaceSlug && projectSegment && isTicketRouteSegment(shortTicketSegment));
  const view = params.get("view") || "";
  const moduleParam = params.get("module") || "";
  const topic = params.get("topic") || "";
  const tabParam = params.get("tab") || "";
  const isWorkspaceLoginReturn = params.get("workspace-login") === "1";
  const documentationMode = !isWorkspaceLoginReturn && (view === "documentation" || ["Documentation", "Docs"].includes(moduleParam));
  const isProjectRoute = Boolean(workspaceSlug && projectSegment && routedModule && !isShortTicketRoute);
  const isProjectIndependentRoute = isProjectRoute && PROJECT_INDEPENDENT_MODULES.has(routedModule);
  const isRootModuleRoute = Boolean(workspaceSlug && rootModule && !isProjectRoute);
  const activeNav = isShortTicketRoute ? "Work" : (isProjectRoute ? routedModule : (isRootModuleRoute ? rootModule : (moduleTabs[moduleParam] || moduleParam === "Logout" ? moduleParam : "Home")));
  const isProjectIndependentNav = PROJECT_INDEPENDENT_MODULES.has(activeNav);
  const tab = moduleTabs[activeNav]?.includes(tabParam) ? tabParam : moduleTabs[activeNav]?.[0] || "Overview";

  return {
    activeNav,
    documentationLabel: topic || tabParam || "Product Blueprint",
    documentationMode,
    selectedProject: isShortTicketRoute ? `project:${projectSegment}` : (isProjectRoute && !isProjectIndependentRoute ? `project:${projectSegment}` : (isProjectIndependentNav ? "" : (params.get("project") || ""))),
    tab: isShortTicketRoute ? "Tickets" : ((isProjectRoute || isRootModuleRoute) ? (moduleTabs[activeNav]?.find((tabName) => toRouteSegment(tabName) === (isProjectRoute ? routedTab : rootTab)) || moduleTabs[activeNav]?.[0] || "Overview") : tab),
    routeProjectId: isShortTicketRoute ? projectSegment : (isProjectRoute && !isProjectIndependentRoute ? projectSegment : ""),
    routeRecordId: isShortTicketRoute ? shortTicketSegment : (isProjectRoute && !isProjectIndependentRoute ? ticketSegment : ""),
    focusedCommentId: params.get("focusedCommentId") || "",
    activeBoardId: params.get("board") || "",
  };
};

function App() {
  const initialUrlState = useMemo(() => getWorkspaceUrlState(), []);
  const urlSyncReady = useRef(false);
  const lastAnalyticsPageRef = useRef("");
  const initialWorkspaceAccessNotice = useMemo(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("workspace-access") === "required",
    []
  );
  const themeSaveTimer = useRef(null);
  const [activeNav, setActiveNav] = useState(initialUrlState.activeNav);
  const [workView, setWorkView] = useState("List");
  const [activeSubtabs, setActiveSubtabs] = useState(() =>
    Object.fromEntries(Object.entries(moduleTabs).map(([moduleName, tabs]) => [
      moduleName,
      moduleName === initialUrlState.activeNav ? initialUrlState.tab : tabs[0],
    ]))
  );
  const [, setDocumentationOpen] = useState(false);
  const [documentationLabel, setDocumentationLabel] = useState(initialUrlState.documentationLabel);
  const [documentationMode, setDocumentationMode] = useState(initialUrlState.documentationMode);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [themeMode, setThemeMode] = useState("default");
  const [appearanceMode, setAppearanceMode] = useState(getSavedWorkspaceAppearance);
  const [systemAppearanceMode, setSystemAppearanceMode] = useState(getSystemWorkspaceAppearance);
  const [publicTheme, setPublicTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    const savedTheme = window.localStorage.getItem("lyriclious-workspace-public-theme-choice");
    return savedTheme === "dark" || savedTheme === "light" ? savedTheme : "dark";
  });
  const [organizationColor, setOrganizationColor] = useState(LYRICLIOUS_THEME);
  const [themeSaveStatus, setThemeSaveStatus] = useState("");
  const [workspaceInfo, setWorkspaceInfo] = useState(null);
  const [workspaceAdministration, setWorkspaceAdministration] = useState(null);
  const [workspaceRecords, setWorkspaceRecords] = useState([]);
  const cacheOverviewRecords = useCallback((overviewRecords) => {
    setWorkspaceRecords((current) => {
      const knownIds = new Set(current.map((record) => record.id));
      const missing = overviewRecords.filter((record) => !knownIds.has(record.id));
      return missing.length ? [...current, ...missing] : current;
    });
  }, []);
  const [workspaceActivity, setWorkspaceActivity] = useState([]);
  const [workspaceUser, setWorkspaceUser] = useState(null);
  const [sidebarEmailCopied, setSidebarEmailCopied] = useState(false);
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  const [workspaceError, setWorkspaceError] = useState("");
  const [workspaceLoadAttempt, setWorkspaceLoadAttempt] = useState(0);
  const [workspaceOnboardingPrompt, setWorkspaceOnboardingPrompt] = useState(initialWorkspaceAccessNotice);
  const [liveNow, setLiveNow] = useState(() => new Date());
  const [selectedProject, setSelectedProject] = useState(initialUrlState.selectedProject);
  const [routeProjectId, setRouteProjectId] = useState(initialUrlState.routeProjectId || "");
  const [routeRecordId, setRouteRecordId] = useState(initialUrlState.routeRecordId || "");
  const [focusedCommentId, setFocusedCommentId] = useState(initialUrlState.focusedCommentId || "");
  const [activeBoardId, setActiveBoardId] = useState(initialUrlState.activeBoardId || "");
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [projectCreateTitle, setProjectCreateTitle] = useState("");
  const [projectCreateSaving, setProjectCreateSaving] = useState(false);

  const [projectCreateError, setProjectCreateError] = useState("");
  const [projectCreateDialogOpen, setProjectCreateDialogOpen] = useState(false);
  const [quickTicketOpen, setQuickTicketOpen] = useState(false);
  const [quickTicket, setQuickTicket] = useState({ title: "", description: "", priority: "Medium" });
  const [quickTicketSaving, setQuickTicketSaving] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportType, setReportType] = useState("bug");
  const [reportTitle, setReportTitle] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [reportSaving, setReportSaving] = useState(false);
  const [reportMessage, setReportMessage] = useState("");
  const [reportError, setReportError] = useState("");
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [logoutError, setLogoutError] = useState("");
  const [sidebarTooltip, setSidebarTooltip] = useState(null);
  const [instantTooltip, setInstantTooltip] = useState(null);
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [globalSearchPeriod, setGlobalSearchPeriod] = useState("any");
  const [globalSearchAssignees, setGlobalSearchAssignees] = useState([]);
  const [globalSearchReportedByMe, setGlobalSearchReportedByMe] = useState(false);
  const [globalSearchStatuses, setGlobalSearchStatuses] = useState([]);
  const [globalSearchLabel, setGlobalSearchLabel] = useState("");
  const [globalSearchPeople, setGlobalSearchPeople] = useState([]);
  const [peopleDirectorySearchQuery, setPeopleDirectorySearchQuery] = useState("");
  const searchShortcutLabel = typeof navigator !== "undefined" && /mac/i.test(navigator.platform) ? "⌘ K" : "Ctrl K";
  const globalSearchRef = useRef(null);
  const globalSearchContainerRef = useRef(null);
  const projectSwitcherRef = useRef(null);
  const instantTooltipTargetRef = useRef(null);

  const togglePublicTheme = () => {
    setPublicTheme((current) => {
      const nextTheme = current === "dark" ? "light" : "dark";
      if (typeof window !== "undefined") {
        window.localStorage.setItem("lyriclious-workspace-public-theme-choice", nextTheme);
      }
      return nextTheme;
    });
  };

  const runtimeModules = useMemo(
    () => mergeRuntimeModules(workspaceAdministration?.modules || []),
    [workspaceAdministration?.modules]
  );
  const moduleStateMap = useMemo(
    () => Object.fromEntries(runtimeModules.map((module) => [module.key, module])),
    [runtimeModules]
  );
  const hasDeferredModuleAccess = workspaceUser?.canPreviewDeferredModules === true;
  const isClientPortalUser = workspaceUser?.isClient === true;
  const hasPrivateOwnerModuleAccess = PRIVATE_OWNER_MODULE_EMAILS.has(normalizeWorkspaceEmail(workspaceUser?.email || workspaceUser?.workEmail || workspaceUser?.primaryEmail))
    && workspaceUser?.accessLevel === "Owner";
  const canUseWorkspaceModule = useCallback((moduleName) => {
    const moduleConfig = moduleStateMap[moduleName];
    if (!moduleConfig) return false;
    if (PRIVATE_OWNER_MODULES.has(moduleName)) return hasPrivateOwnerModuleAccess;
    if (moduleConfig.enabled === false && !(PRIVATE_OWNER_MODULES.has(moduleName) && hasPrivateOwnerModuleAccess)) return false;
    if (moduleConfig.comingSoon === true && !hasDeferredModuleAccess) return false;
    return true;
  }, [hasDeferredModuleAccess, hasPrivateOwnerModuleAccess, moduleStateMap]);
  const runtimeModuleTabs = useMemo(
    () => ({
      ...Object.fromEntries(runtimeModules.map((module) => {
        const enabledTabs = (module.tabs || []).filter((tab) => tab.enabled !== false);
        if (enabledTabs.length || !(PRIVATE_OWNER_MODULES.has(module.key) && hasPrivateOwnerModuleAccess)) {
          return [module.key, enabledTabs];
        }
        return [module.key, (moduleTabs[module.key] || []).map((tab, index) => ({ key: `${module.key}-${tab}-${index}`, name: tab, label: tab, enabled: true, premium: false, order: index + 1 }))];
      })),
      Settings: (moduleTabs.Settings || []).map((tab, index) => ({ key: `Settings-${tab}-${index}`, name: tab, label: tab, enabled: true, premium: false, order: index + 1 })),
    }),
    [hasPrivateOwnerModuleAccess, runtimeModules]
  );
  const visibleNavItems = useMemo(
    () => {
      const navigationMeta = new Map(navItems.map((item) => [item.label, item]));
      return runtimeModules
      .map((module) => {
        const metadata = navigationMeta.get(module.key);
        return {
          label: module.key,
          displayLabel: module.label || module.key,
          icon: metadata?.icon || Description,
          comingSoon: metadata?.comingSoon === true || module.comingSoon === true || (PRIVATE_OWNER_MODULES.has(module.key) && module.enabled === false),
          sidebarHidden: metadata?.sidebarHidden === true || module.key === "Support Desk",
          order: module.order || 0,
        };
      })
      .filter((item) => item.sidebarHidden !== true
        && canUseWorkspaceModule(item.label)
        && (item.label === "My Team" ? workspaceUser?.canManageTeam === true : workspaceUser?.accessLevel === "Owner" || (workspaceUser?.moduleAccess || []).includes(item.label))
        && (CLIENT_SPACE_MODULES.has(item.label) ? isClientPortalUser : !isClientPortalUser)
        && (item.label !== "My Team" || workspaceUser?.canManageTeam === true || /\b(owner|admin|manager|lead|hr|human resources)\b/i.test(`${workspaceUser?.accessLevel || ""} ${workspaceUser?.workspaceRole || ""}`)))
      .sort((a, b) => {
        const aLocked = a.comingSoon === true || moduleStateMap[a.label]?.comingSoon === true;
        const bLocked = b.comingSoon === true || moduleStateMap[b.label]?.comingSoon === true;
        if (aLocked !== bLocked) return aLocked ? 1 : -1;
        return a.order - b.order;
      });
    },
    [canUseWorkspaceModule, isClientPortalUser, moduleStateMap, runtimeModules, workspaceUser]
  );
  const firstVisibleModule = visibleNavItems[0]?.label || "Home";
  const personalNavItems = visibleNavItems.filter((item) => ["Home", "My Team"].includes(item.label));
  const clientNavItems = visibleNavItems.filter((item) => CLIENT_SPACE_MODULES.has(item.label));
  const workspaceNavItems = visibleNavItems.filter((item) => !["Home", "My Team"].includes(item.label) && !CLIENT_SPACE_MODULES.has(item.label));
  const globalSearchAssigneesList = useMemo(() => Array.from(new Set(workspaceRecords.map((record) => record.owner).filter(Boolean))).slice(0, 8), [workspaceRecords]);
  const globalSearchLabelsList = useMemo(() => Array.from(new Set(workspaceRecords.flatMap((record) => Array.isArray(record.labels) ? record.labels : [record.label]).filter(Boolean))).slice(0, 12), [workspaceRecords]);
  useEffect(() => {
    if (!workspaceUser) {
      setGlobalSearchPeople([]);
      return undefined;
    }

    let active = true;
    getWorkspacePeople()
      .then((data) => {
        if (active) setGlobalSearchPeople(Array.isArray(data.people) ? data.people : []);
      })
      .catch(() => {
        if (active) setGlobalSearchPeople([]);
      });

    return () => {
      active = false;
    };
  }, [workspaceInfo?.id, workspaceUser]);

  const globalSearchResults = useMemo(() => {
    const query = globalSearchQuery.trim().toLowerCase();
    const now = Date.now();
    const periodDays = { today: 1, yesterday: 2, week: 7, month: 30, year: 365 };
    const matchesPeriod = (record) => {
      if (globalSearchPeriod === "any" || !periodDays[globalSearchPeriod]) return true;
      const changedAt = new Date(record.updatedAt || record.createdAt || 0).getTime();
      if (!changedAt) return globalSearchPeriod === "any";
      return now - changedAt <= periodDays[globalSearchPeriod] * 86400000;
    };
    const navigation = visibleNavItems.filter((item) => canUseWorkspaceModule(item.label)).map((item) => ({
      id: `nav-${item.label}`,
      title: item.label,
      detail: "Workspace area",
      moduleName: item.label,
      tabName: runtimeModuleTabs[item.label]?.[0]?.name || "Overview",
      kind: "Navigation",
    }));
    const records = workspaceRecords.filter((record) => {
      const recordLabels = Array.isArray(record.labels) ? record.labels : [record.label];
      const isMine = [workspaceUser?.name, workspaceUser?.email].filter(Boolean).includes(record.owner);
      const normalizedStatus = String(record.status || "").toLowerCase();
      return matchesPeriod(record)
        && (!globalSearchAssignees.length || globalSearchAssignees.includes(record.owner))
        && (!globalSearchReportedByMe || isMine)
        && (!globalSearchStatuses.length || globalSearchStatuses.some((status) => status === "done" ? ["done", "closed", "resolved", "complete", "completed"].includes(normalizedStatus) : !["done", "closed", "resolved", "complete", "completed"].includes(normalizedStatus)))
        && (!globalSearchLabel || recordLabels.includes(globalSearchLabel));
    }).map((record) => {
      const isTicket = isRoutableTicketRecord(record);
      const routeProjectId = isTicket ? getProjectRouteSegment(record, workspaceRecords) : "";
      const routeRecordId = isTicket ? getRecordRouteSegment(record) : "";
      const ticketKey = record.key || routeRecordId;
      return {
        id: `record-${record.id}`,
        title: record.title || "Untitled record",
        detail: [ticketKey, record.type, record.owner, record.status].filter(Boolean).join(" · ") || "Workspace record",
        moduleName: record.moduleName || "Work",
        tabName: isTicket ? "Tickets" : (record.tabName || record.tab || "Overview"),
        kind: record.type || "Record",
        projectId: record.projectId || "",
        routeProjectId,
        routeRecordId,
        searchText: [
          record.title,
          record.description,
          record.key,
          record.type,
          record.owner,
          record.creator,
          record.status,
          record.projectRef,
          record.epic,
          record.sprint,
          record.moduleName,
        ].filter(Boolean).join(" "),
      };
    });
    const people = globalSearchPeople.map((person) => {
      const title = person.name || person.fullName || person.displayName || person.email || "Workspace user";
      const email = person.email || "";
      const role = person.workspaceRole || person.designation || person.accessLevel || "";
      const department = person.department || person.team || "";
      return {
        id: `person-${person.id || person.userId || email || title}`,
        title,
        detail: [email, role, department, person.status].filter(Boolean).join(" · ") || "Workspace person",
        moduleName: "People",
        tabName: "Directory",
        kind: "Person",
        searchQuery: title !== "Workspace user" ? title : email,
        searchText: [
          title,
          email,
          role,
          department,
          person.employeeCode,
          person.location,
          person.status,
        ].filter(Boolean).join(" "),
      };
    });
    const results = [...navigation, ...records, ...people];
    if (!query) return results.slice(0, 8);
    return results.filter((item) => `${item.title} ${item.detail} ${item.moduleName} ${item.kind} ${item.searchText || ""}`.toLowerCase().includes(query)).slice(0, 12);
  }, [canUseWorkspaceModule, globalSearchAssignees, globalSearchLabel, globalSearchPeople, globalSearchPeriod, globalSearchQuery, globalSearchReportedByMe, globalSearchStatuses, runtimeModuleTabs, visibleNavItems, workspaceRecords, workspaceUser?.email, workspaceUser?.name]);
  const globalSearchShortcuts = useMemo(() => [
    { label: "Boards", moduleName: "Work", tabName: "Overview" },
    { label: "Projects", moduleName: "Projects", tabName: "Overview" },
    { label: "People", moduleName: "People", tabName: "Overview" },
    { label: "Docs", moduleName: "Knowledge", tabName: "Overview" },
  ].filter((item) => canUseWorkspaceModule(item.moduleName)), [canUseWorkspaceModule]);

  const openGlobalSearchResult = (result) => {
    if (!canUseWorkspaceModule(result.moduleName)) return;
    setDocumentationMode(false);
    setActiveNav(result.moduleName);
    const availableTabs = runtimeModuleTabs[result.moduleName] || [];
    const tab = availableTabs.some((item) => item.name === result.tabName)
      ? result.tabName
      : availableTabs[0]?.name;
    if (tab) setActiveSubtabs((current) => ({ ...current, [result.moduleName]: tab }));
    if (!(result.moduleName === "Work" && tab === "Board")) setActiveBoardId("");
    if (result.kind === "Person") {
      setRouteRecordId("");
      setRouteProjectId("");
      setFocusedCommentId("");
      setPeopleDirectorySearchQuery(result.searchQuery || result.title || "");
    } else if (result.routeRecordId) {
      if (result.routeProjectId) setRouteProjectId(result.routeProjectId);
      if (result.projectId) setSelectedProject(`project:${result.projectId}`);
      setRouteRecordId(result.routeRecordId);
      setFocusedCommentId("");
    } else {
      setRouteRecordId("");
      setFocusedCommentId("");
    }
    setGlobalSearchOpen(false);
    setGlobalSearchQuery("");
  };

  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setGlobalSearchOpen(true);
        window.setTimeout(() => globalSearchRef.current?.focus(), 0);
      }
      if (event.key === "Escape") setGlobalSearchOpen(false);
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  useEffect(() => {
    const handleOutsidePointer = (event) => {
      if (!globalSearchContainerRef.current?.contains(event.target)) {
        setGlobalSearchOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleOutsidePointer, true);
    return () => document.removeEventListener("pointerdown", handleOutsidePointer, true);
  }, []);

  useEffect(() => {
    const handleOutsidePointer = (event) => {
      if (!projectSwitcherRef.current?.contains(event.target)) {
        setProjectMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleOutsidePointer, true);
    return () => document.removeEventListener("pointerdown", handleOutsidePointer, true);
  }, []);

  useEffect(() => {
    const handleOutsideDetailsPointer = (event) => {
      document.querySelectorAll(".workspace-shell details[open]").forEach((detailsNode) => {
        if (!detailsNode.contains(event.target)) detailsNode.open = false;
      });
    };
    document.addEventListener("pointerdown", handleOutsideDetailsPointer, true);
    return () => document.removeEventListener("pointerdown", handleOutsideDetailsPointer, true);
  }, []);

  useEffect(() => {
    if (!workspaceUser) return undefined;
    const heartbeat = () => {
      if (document.visibilityState === "visible") heartbeatWorkspaceAttendance().catch(() => {});
    };
    heartbeat();
    const timer = window.setInterval(heartbeat, 60000);
    document.addEventListener("visibilitychange", heartbeat);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", heartbeat); };
  }, [workspaceUser]);

  useEffect(() => {
    if (!workspaceUser || documentationMode) return undefined;
    const moduleName = activeNav;
    const tabName = activeSubtabs[activeNav] || runtimeModuleTabs[activeNav]?.[0]?.name || moduleTabs[activeNav]?.[0] || "Overview";
    const startedAt = Date.now();
    const timer = window.setTimeout(() => {
      if (document.visibilityState !== "visible") return;
      trackWorkspacePageVisit({ moduleName, tabName, pagePath: window.location.pathname, durationSeconds: Math.floor((Date.now() - startedAt) / 1000) }).catch(() => {});
    }, 30000);
    return () => window.clearTimeout(timer);
  }, [activeNav, activeSubtabs, documentationMode, runtimeModuleTabs, workspaceUser]);

  useEffect(() => {
    document.body.classList.toggle("workspace-sidebar-expanded", sidebarExpanded);
    setSidebarTooltip(null);
    return () => document.body.classList.remove("workspace-sidebar-expanded");
  }, [sidebarExpanded]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(max-width: 760px)");
    const collapseSidebarForMobile = () => {
      if (media.matches) setSidebarExpanded(false);
    };
    collapseSidebarForMobile();
    media.addEventListener?.("change", collapseSidebarForMobile);
    return () => media.removeEventListener?.("change", collapseSidebarForMobile);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setLiveNow(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, [runtimeModuleTabs]);

  useEffect(() => {
    let mounted = true;
    const returningFromLogin = isWorkspaceSsoReturn() || window.sessionStorage.getItem("lyriclious-workspace-login-return") === "1";
    const pathWorkspaceSlug = getWorkspaceSlugFromPath();
    const rememberedWorkspaceSlug = getRememberedWorkspaceSlug();
    const loadWorkspace = async () => {
      if (!pathWorkspaceSlug && rememberedWorkspaceSlug) {
        const workspaceUrl = new URL(window.location.href);
        workspaceUrl.pathname = `/${encodeURIComponent(rememberedWorkspaceSlug)}`;
        window.history.replaceState({}, "", `${workspaceUrl.pathname}${workspaceUrl.search}${workspaceUrl.hash}`);
      }

      let lastError = null;
      const hasWorkspaceContext = Boolean(pathWorkspaceSlug || rememberedWorkspaceSlug);
      const attempts = returningFromLogin || hasWorkspaceContext ? 4 : 1;

      for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
          const data = await getWorkspaceBootstrap();
          if (!mounted) return;
          setWorkspaceRecords(data.records || []);
          setWorkspaceActivity(data.activity || []);
          setWorkspaceUser(data.currentUser || null);
          setWorkspaceInfo(data.workspace || null);
          setWorkspaceAdministration(data.workspace?.administration || null);
          if (data.workspace?.branding?.color_theme_mode || data.workspace?.branding?.theme_mode) {
            setThemeMode(data.workspace.branding.color_theme_mode || data.workspace.branding.theme_mode);
          }
          const userAppearanceMode = data.currentUser?.accountPreferences?.workspace?.appearance_mode || data.currentUser?.accountPreferences?.workspace?.appearanceMode;
          if (["light", "dark", "system"].includes(userAppearanceMode)) {
            setAppearanceMode(userAppearanceMode);
            window.localStorage.setItem(workspaceAppearanceStorageKey, userAppearanceMode);
          }
          if (data.workspace?.branding?.primary_color) setOrganizationColor(data.workspace.branding.primary_color);
          if (data.workspace?.slug) {
            rememberWorkspaceSlug(data.workspace.slug);
            const canonicalWorkspaceSegment = encodeURIComponent(data.workspace.slug);
            const currentSegments = window.location.pathname.split("/").filter(Boolean);
            if (!currentSegments.length) {
              window.history.replaceState({}, "", `/${canonicalWorkspaceSegment}${window.location.search}${window.location.hash}`);
            } else if (currentSegments[0] !== data.workspace.slug) {
              currentSegments[0] = canonicalWorkspaceSegment;
              window.history.replaceState({}, "", `/${currentSegments.join("/")}${window.location.search}${window.location.hash}`);
            }
          }
          if (returningFromLogin) {
            setDocumentationMode(false);
            setDocumentationOpen(false);
            setDocumentationLabel("Product Blueprint");
            setActiveNav("Home");
            setActiveSubtabs((current) => ({ ...current, Home: moduleTabs.Home?.[0] || "Overview" }));
          }
          window.sessionStorage.removeItem("lyriclious-workspace-login-return");
          clearWorkspaceSsoReturn();
          setWorkspaceError("");
          return;
        } catch (error) {
          lastError = error;
          const shouldRecoverFromStaleWorkspacePath =
            returningFromLogin &&
            error?.response?.status === 404 &&
            /workspace not found/i.test(error?.response?.data?.message || "") &&
            Boolean(getWorkspaceSlugFromPath());

          if (shouldRecoverFromStaleWorkspacePath) {
            const workspaceUrl = new URL(window.location.href);
            workspaceUrl.pathname = "/";
            workspaceUrl.search = "?workspace-login=1";
            workspaceUrl.hash = "";
            window.history.replaceState({}, "", `${workspaceUrl.pathname}${workspaceUrl.search}`);
            continue;
          }

          if (attempt < attempts - 1) {
            await new Promise((resolve) => window.setTimeout(resolve, 350 * (attempt + 1)));
          }
        }
      }

      const shouldRecoverFromMissingRootSlug =
        !pathWorkspaceSlug &&
        Boolean(rememberedWorkspaceSlug) &&
        (lastError?.response?.status === 403 || lastError?.response?.status === 404);

      if (shouldRecoverFromMissingRootSlug) {
        const workspaceUrl = new URL(window.location.href);
        workspaceUrl.pathname = `/${encodeURIComponent(rememberedWorkspaceSlug)}`;
        window.history.replaceState({}, "", `${workspaceUrl.pathname}${workspaceUrl.search}${workspaceUrl.hash}`);
        if (mounted) {
          setWorkspaceLoading(true);
          setWorkspaceLoadAttempt((attempt) => attempt + 1);
        }
        return;
      }

      const needsWorkspaceOnboarding =
        returningFromLogin &&
        lastError?.response?.status === 403 &&
        /workspace access is not enabled/i.test(lastError?.response?.data?.message || "");

      if (needsWorkspaceOnboarding) {
        try {
          await captureWorkspaceOnboardingLead({ pagePath: window.location.href });
        } catch (error) {
          // The onboarding choices remain available even if the lead service is temporarily unavailable.
        }
      }

      if (mounted) {
        setWorkspaceOnboardingPrompt(needsWorkspaceOnboarding);
        setWorkspaceError(lastError?.response?.data?.message || "Please sign in to load Workspace data.");
      }
    };

    loadWorkspace()
      .finally(() => {
        if (mounted) setWorkspaceLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [workspaceLoadAttempt]);

  useEffect(() => () => {
    if (themeSaveTimer.current) window.clearTimeout(themeSaveTimer.current);
  }, []);

  useEffect(() => {
    if (!workspaceLoading && !workspaceUser && documentationLabel === "Product Blueprint") {
      setDocumentationLabel("Dashboard");
    }
  }, [documentationLabel, workspaceLoading, workspaceUser]);

  const activeModuleTab = activeSubtabs[activeNav] || runtimeModuleTabs[activeNav]?.[0]?.name || moduleTabs[activeNav]?.[0];
  const isGeneralNav = generalItems.some((item) => item.label === activeNav);
  const isProjectIndependentActiveNav = PROJECT_INDEPENDENT_MODULES.has(activeNav);

  useEffect(() => {
    const handlePopState = () => {
      const next = getWorkspaceUrlState();
      setDocumentationMode(next.documentationMode);
      setDocumentationLabel(next.documentationLabel);
      setSelectedProject(next.selectedProject);
      setRouteProjectId(next.routeProjectId || "");
      setRouteRecordId(next.routeRecordId || "");
      setFocusedCommentId(next.focusedCommentId || "");
      setActiveBoardId(next.activeBoardId || "");
      setActiveNav(next.activeNav);
      if (next.tab && (runtimeModuleTabs[next.activeNav] || moduleTabs[next.activeNav])) {
        setActiveSubtabs((current) => ({ ...current, [next.activeNav]: next.tab }));
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [runtimeModuleTabs]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams();
    const workspaceSlug = window.location.pathname.split("/").filter(Boolean)[0] || workspaceInfo?.slug || "workspace";
    const selectedProjectId = `${selectedProject || ""}`.replace(/^project:/, "");
    const selectedProjectRecord = workspaceRecords.find((record) => `${record.id || ""}` === selectedProjectId);
    const projectSegment = isProjectIndependentActiveNav ? "" : (routeProjectId || selectedProjectRecord?.projectSlug || "");
    let pathname = `/${workspaceSlug}`;

    if (routeRecordId && !documentationMode && activeNav === "Work") {
      if (!projectSegment) return;
      pathname = `/${workspaceSlug}/${projectSegment}/${routeRecordId}`;
      if (focusedCommentId) params.set("focusedCommentId", focusedCommentId);
    } else if (documentationMode) {
      params.set("view", "documentation");
      params.set("topic", documentationLabel || "Product Blueprint");
    } else {
      pathname = isProjectIndependentActiveNav
        ? `/${workspaceSlug}/${toRouteSegment(activeNav)}/${toRouteSegment(activeModuleTab || "overview")}`
        : projectSegment
        ? `/${workspaceSlug}/${projectSegment}/${toRouteSegment(activeNav || "Home")}/${toRouteSegment(activeModuleTab || "overview")}`
        : `/${workspaceSlug}/${toRouteSegment(activeNav || "Home")}/${toRouteSegment(activeModuleTab || "overview")}`;
      if (activeNav === "Work" && activeModuleTab === "Board" && activeBoardId) {
        params.set("board", activeBoardId);
      }
    }

    const nextUrl = `${pathname}${params.toString() ? `?${params.toString()}` : ""}${window.location.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const analyticsPage = `${pathname}${params.toString() ? `?${params.toString()}` : ""}`;
    if (isWorkspaceProduction() && typeof window.gtag === "function" && lastAnalyticsPageRef.current !== analyticsPage) {
      window.gtag("event", "page_view", {
        page_location: `${window.location.origin}${nextUrl}`,
        page_path: analyticsPage,
        send_to: WORKSPACE_GA_MEASUREMENT_ID,
      });
      lastAnalyticsPageRef.current = analyticsPage;
    }
    if (nextUrl === currentUrl) {
      urlSyncReady.current = true;
      return;
    }

    if (urlSyncReady.current) {
      window.history.pushState({}, "", nextUrl);
    } else {
      window.history.replaceState({}, "", nextUrl);
      urlSyncReady.current = true;
    }

  }, [activeBoardId, activeModuleTab, activeNav, documentationLabel, documentationMode, focusedCommentId, isProjectIndependentActiveNav, routeProjectId, routeRecordId, selectedProject, workspaceInfo?.slug, workspaceRecords]);

  useEffect(() => {
    if (activeNav === "Work" || !routeRecordId) return;
    setRouteRecordId("");
    setFocusedCommentId("");
  }, [activeNav, routeRecordId]);

  useEffect(() => {
    if (workspaceLoading) return;
    if (documentationMode || isGeneralNav || activeNav === "Logout" || activeNav === "Settings") return;
    if (!canUseWorkspaceModule(activeNav)) {
      setActiveNav(firstVisibleModule);
      return;
    }
    const availableTabs = runtimeModuleTabs[activeNav] || [];
    if (!availableTabs.length) return;
    const currentTab = activeSubtabs[activeNav];
    if (!availableTabs.some((tab) => tab.name === currentTab)) {
      const firstUsableTab = availableTabs.find((tab) => tab.premium !== true)?.name || availableTabs[0]?.name;
      if (firstUsableTab) {
        setActiveSubtabs((current) => ({ ...current, [activeNav]: firstUsableTab }));
      }
    }
  }, [activeNav, activeSubtabs, canUseWorkspaceModule, documentationMode, firstVisibleModule, isGeneralNav, runtimeModuleTabs, workspaceLoading]);

  const projectClientOptions = useMemo(() => {
    const seen = new Set();
    const seenRoutes = new Set();
    const seenTitles = new Set();
    const recordOptions = workspaceRecords
      .filter((record) =>
        record.moduleName === "Projects" ||
        `${record.type || ""}`.toLowerCase().includes("project")
      )
      .map((record) => {
        return {
          key: `project:${record.id || record.title}`,
          title: record.title,
          displayTitle: getProjectDisplayTitle(record),
          type: "project",
          moduleName: "Projects",
          tab: "Active",
          routeKey: record.projectSlug || toRouteSegment(record.title),
          branding: record.projectBranding || {},
          icon: getProjectBrandingIcon(record.projectBranding || {}),
        };
      })
      .filter((record) => {
        if (!record.title || seen.has(record.key)) return false;
        seen.add(record.key);
        seenRoutes.add(record.routeKey);
        seenTitles.add(`${record.title}`.trim().toLowerCase());
        return true;
      });

    const assignedProjectEntries = [
      ...(workspaceUser?.projectAssignments || []).map((assignment) => ({
        projectId: assignment.projectId,
        projectRef: assignment.projectRef,
      })),
      ...(workspaceUser?.projectRefs || []).map((projectRef) => ({ projectRef })),
    ];

    const assignmentOptions = assignedProjectEntries
      .map((assignment) => {
        const title = `${assignment.projectRef || ""}`.trim();
        const routeKey = toRouteSegment(title);
        const key = assignment.projectId ? `project:${assignment.projectId}` : `project-ref:${routeKey}`;
        return title && routeKey ? {
          key,
          title,
          displayTitle: title,
          type: "project",
          moduleName: "Projects",
          tab: "Active",
          routeKey,
          branding: {},
          icon: "",
          virtualProjectContext: true,
        } : null;
      })
      .filter(Boolean)
      .filter((record) => {
        const titleKey = `${record.title}`.trim().toLowerCase();
        if (seen.has(record.key) || seenRoutes.has(record.routeKey) || seenTitles.has(titleKey)) return false;
        seen.add(record.key);
        seenRoutes.add(record.routeKey);
        seenTitles.add(titleKey);
        return true;
      });

    return [...recordOptions, ...assignmentOptions];
  }, [workspaceRecords, workspaceUser?.projectAssignments, workspaceUser?.projectRefs]);
  const routeProjectOption = useMemo(() => {
    if (!routeProjectId) return null;
    return projectClientOptions.find((option) => option.key === `project:${routeProjectId}` || option.routeKey === routeProjectId) || null;
  }, [projectClientOptions, routeProjectId]);
  const selectedProjectOption = projectClientOptions.find((option) => option.key === selectedProject) || routeProjectOption || (!routeProjectId ? projectClientOptions[0] : null);
  const selectedProjectRecordId = selectedProjectOption?.key?.startsWith("project:") ? selectedProjectOption.key.slice("project:".length) : "";
  const selectedProjectTitle = selectedProjectOption?.title || "";
  const selectedProjectMatchKeys = useMemo(() => new Set([
    selectedProjectOption?.title,
    selectedProjectOption?.displayTitle,
    selectedProjectOption?.routeKey,
  ].map((value) => `${value || ""}`.trim().toLowerCase()).filter(Boolean)), [selectedProjectOption]);
  const selectedProjectBranding = selectedProjectOption?.branding || {};

  useEffect(() => {
    if (!routeProjectOption || selectedProject === routeProjectOption.key) return;
    setSelectedProject(routeProjectOption.key);
  }, [routeProjectOption, selectedProject]);

  useEffect(() => {
    if (!routeRecordId || !workspaceRecords.length) return;
    const normalizedRouteRecordId = `${routeRecordId}`.trim().toLowerCase();
    const ticket = workspaceRecords.find((record) =>
      record.ticketSequence
      && (`${record.id}` === `${routeRecordId}` || `${record.key || ""}`.trim().toLowerCase() === normalizedRouteRecordId)
      && recordMatchesProjectRoute(record, routeProjectId, workspaceRecords)
    );
    const projectSlug = ticket ? getProjectRouteSegment(ticket, workspaceRecords) : "";
    if (!ticket || !projectSlug || routeProjectId === projectSlug) return;

    setRouteProjectId(projectSlug);
    if (ticket.projectId) setSelectedProject(`project:${ticket.projectId}`);
  }, [routeProjectId, routeRecordId, workspaceRecords]);

  useEffect(() => {
    if (!isProjectIndependentActiveNav) return;
    if (selectedProject) setSelectedProject("");
    if (routeProjectId) setRouteProjectId("");
    if (routeRecordId) setRouteRecordId("");
    if (focusedCommentId) setFocusedCommentId("");
    if (activeBoardId) setActiveBoardId("");
    if (projectMenuOpen) setProjectMenuOpen(false);
  }, [activeBoardId, focusedCommentId, isProjectIndependentActiveNav, projectMenuOpen, routeProjectId, routeRecordId, selectedProject]);

  const isFreeWorkspacePlan = (workspaceInfo?.plan || "Free") === "Free";
  const freeProjectLimit = Number(workspaceInfo?.limits?.freeProjectLimit ?? 1);
  const projectUsageCount = Number.isFinite(Number(workspaceInfo?.administration?.overview?.usage?.projects?.used))
    ? Number(workspaceInfo.administration.overview.usage.projects.used)
    : projectClientOptions.filter((option) => option.type === "project").length;
  const canManageProjectClient = Boolean(workspaceUser?.canManageWorkspace);
  const canCreateProject = canManageProjectClient && (!isFreeWorkspacePlan || projectUsageCount < freeProjectLimit);
  const activeProjectColor = selectedProjectBranding?.color_theme_mode === "project" && /^#[0-9a-fA-F]{6}$/.test(selectedProjectBranding?.primary_color || "")
    ? selectedProjectBranding.primary_color
    : "";
  const activeThemeColor = !isProjectIndependentActiveNav && activeProjectColor
    ? activeProjectColor
    : themeMode === "organization"
      ? organizationColor
      : LYRICLIOUS_THEME;
  const activeAppearanceMode = appearanceMode === "system" ? systemAppearanceMode : resolveWorkspaceAppearance(appearanceMode);
  const activePanelSoft = activeAppearanceMode === "dark"
    ? `color-mix(in srgb, ${activeThemeColor} 12%, #161618)`
    : `color-mix(in srgb, ${activeThemeColor} 9%, #ffffff)`;
  const themeStyle = useMemo(() => ({
    "--brand": activeThemeColor,
    "--brand-dark": activeThemeColor,
    "--bg": activeAppearanceMode === "dark" ? "#050505" : "#f7faf9",
    "--panel": activeAppearanceMode === "dark" ? "#0b0b0c" : "#ffffff",
    "--panel-soft": activePanelSoft,
    "--ink": activeAppearanceMode === "dark" ? "#f7f7f7" : "#17211f",
    "--muted": activeAppearanceMode === "dark" ? "#c9c9c9" : "#66736f",
    "--line": activeAppearanceMode === "dark" ? "#2d2d30" : "#dce8e4",
    "--shadow": activeAppearanceMode === "dark" ? "0 18px 50px rgba(0, 0, 0, 0.45)" : "0 18px 50px rgba(15, 23, 42, 0.08)",
  }), [activeAppearanceMode, activePanelSoft, activeThemeColor]);

  useEffect(() => {
    // Portalled dialogs render under document.body, outside .workspace-shell.
    // Mirror the active organization theme on :root so every modal inherits it.
    const documentRoot = document.documentElement;
    documentRoot.style.setProperty("--brand", activeThemeColor);
    documentRoot.style.setProperty("--brand-dark", activeThemeColor);
    documentRoot.style.setProperty("--panel-soft", activePanelSoft);
    documentRoot.style.setProperty("--bg", themeStyle["--bg"]);
    documentRoot.style.setProperty("--panel", themeStyle["--panel"]);
    documentRoot.style.setProperty("--ink", themeStyle["--ink"]);
    documentRoot.style.setProperty("--muted", themeStyle["--muted"]);
    documentRoot.style.setProperty("--line", themeStyle["--line"]);
    documentRoot.dataset.workspaceAppearance = activeAppearanceMode;
  }, [activeAppearanceMode, activePanelSoft, activeThemeColor, themeStyle]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const updateSystemAppearance = () => setSystemAppearanceMode(media.matches ? "dark" : "light");
    updateSystemAppearance();
    media.addEventListener?.("change", updateSystemAppearance);
    return () => media.removeEventListener?.("change", updateSystemAppearance);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const restoreTargetTitle = () => {
      const currentTarget = instantTooltipTargetRef.current;
      if (currentTarget?.dataset?.instantTooltipTitle) {
        currentTarget.setAttribute("title", currentTarget.dataset.instantTooltipTitle);
        delete currentTarget.dataset.instantTooltipTitle;
      }
      instantTooltipTargetRef.current = null;
    };
    const getTooltipTarget = (origin) => {
      if (!(origin instanceof Element)) return null;
      const shell = origin.closest(".workspace-shell");
      if (!shell) return null;
      if (origin.closest(".sidebar-floating-tooltip, .workspace-floating-tooltip, .comment-reaction-tooltip, [role='tooltip']")) return null;
      return origin.closest("[title]");
    };
    const computeTooltipPosition = (target) => {
      const rect = target.getBoundingClientRect();
      return {
        left: Math.min(window.innerWidth - 16, Math.max(16, rect.left + (rect.width / 2))),
        top: Math.max(12, rect.top - 10),
      };
    };
    const showTooltip = (target) => {
      const label = `${target.getAttribute("title") || target.dataset.instantTooltipTitle || ""}`.trim();
      if (!label) return;
      if (instantTooltipTargetRef.current && instantTooltipTargetRef.current !== target) restoreTargetTitle();
      if (!target.dataset.instantTooltipTitle) target.dataset.instantTooltipTitle = label;
      target.removeAttribute("title");
      instantTooltipTargetRef.current = target;
      setInstantTooltip({ label, ...computeTooltipPosition(target) });
    };
    const hideTooltip = (target) => {
      if (target && instantTooltipTargetRef.current && target !== instantTooltipTargetRef.current && target.contains?.(instantTooltipTargetRef.current) === false) return;
      restoreTargetTitle();
      setInstantTooltip(null);
    };
    const handlePointerOver = (event) => {
      const target = getTooltipTarget(event.target);
      if (!target) return;
      showTooltip(target);
    };
    const handlePointerOut = (event) => {
      const activeTarget = instantTooltipTargetRef.current;
      if (!activeTarget) return;
      const nextTarget = event.relatedTarget instanceof Element ? event.relatedTarget : null;
      if (nextTarget && activeTarget.contains(nextTarget)) return;
      hideTooltip(activeTarget);
    };
    const handleFocusIn = (event) => {
      const target = getTooltipTarget(event.target);
      if (!target) return;
      showTooltip(target);
    };
    const handleFocusOut = (event) => {
      const activeTarget = instantTooltipTargetRef.current;
      if (!activeTarget) return;
      const nextTarget = event.relatedTarget instanceof Element ? event.relatedTarget : null;
      if (nextTarget && activeTarget.contains(nextTarget)) return;
      hideTooltip(activeTarget);
    };
    const handleScrollOrResize = () => {
      const activeTarget = instantTooltipTargetRef.current;
      if (!activeTarget) return;
      setInstantTooltip((current) => (current ? { ...current, ...computeTooltipPosition(activeTarget) } : current));
    };
    document.addEventListener("mouseover", handlePointerOver, true);
    document.addEventListener("mouseout", handlePointerOut, true);
    document.addEventListener("focusin", handleFocusIn, true);
    document.addEventListener("focusout", handleFocusOut, true);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      document.removeEventListener("mouseover", handlePointerOver, true);
      document.removeEventListener("mouseout", handlePointerOut, true);
      document.removeEventListener("focusin", handleFocusIn, true);
      document.removeEventListener("focusout", handleFocusOut, true);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
      restoreTargetTitle();
    };
  }, []);

  const workspaceName = workspaceInfo?.name || "Lyriclious Workspace";
  const workspaceBranding = workspaceInfo?.branding || {};
  const customWorkspaceSquareLogo = workspaceBranding.logo_square_url || workspaceBranding.logo_url || "";
  const workspaceFavicon = workspaceBranding.favicon_ico_url || workspaceBranding.favicon_url || DEFAULT_WORKSPACE_ICON;
  const activeProjectIcon = !isProjectIndependentActiveNav ? selectedProjectBranding?.icon_url || "" : "";
  const activeProjectFavicon = !isProjectIndependentActiveNav
    ? selectedProjectBranding?.favicon_ico_url || selectedProjectBranding?.favicon_url || selectedProjectBranding?.icon_url || ""
    : "";
  const activeProjectTitle = !isProjectIndependentActiveNav ? selectedProjectOption?.displayTitle || "" : "";
  const activeHeaderIcon = activeProjectIcon || customWorkspaceSquareLogo;
  const activeFavicon = activeProjectFavicon || workspaceFavicon;
  const workspaceInitial = (workspaceName.trim().charAt(0) || "W").toUpperCase();
  const sidebarUserName = workspaceUser?.name || workspaceUser?.fullName || "Workspace user";
  const sidebarUserEmail = workspaceUser?.email || workspaceUser?.workEmail || workspaceUser?.primaryEmail || "No email";
  const sidebarUserAvatar =
    workspaceUser?.avatar_url ||
    workspaceUser?.avatarUrl ||
    workspaceUser?.profile_image?.url ||
    workspaceUser?.profileImage?.url ||
    workspaceUser?.photo?.url ||
    workspaceUser?.image?.url ||
    workspaceUser?.avatar?.url ||
    workspaceUser?.avatar?.secure_url ||
    workspaceUser?.avatar?.secureUrl ||
    (typeof workspaceUser?.avatar === "string" ? workspaceUser.avatar : "") ||
    "";
  const sidebarUserInitials = sidebarUserName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "U";
  const sidebarUserStats = useMemo(() => {
    const userNames = new Set([workspaceUser?.name, workspaceUser?.fullName, workspaceUser?.user_name, workspaceUser?.email].filter(Boolean).map((value) => `${value}`.trim().toLowerCase()));
    const userId = `${workspaceUser?.id || workspaceUser?._id || ""}`;
    const isUserInvolved = (record) => {
      const assignees = Array.isArray(record.assignees) ? record.assignees : `${record.assignees || ""}`.split(",");
      const contributors = Array.isArray(record.contributors) ? record.contributors : `${record.contributors || ""}`.split(",");
      const members = Array.isArray(record.members) ? record.members : `${record.members || ""}`.split(",");
      const peopleValues = [
        record.owner,
        record.creator,
        record.reporter,
        record.email,
        record.creatorEmail,
        record.assignee,
        record.assigneeName,
        record.createdBy,
        record.updatedBy,
        ...assignees,
        ...contributors,
        ...members,
      ].filter(Boolean).map((value) => `${value}`.trim().toLowerCase());
      return peopleValues.some((value) => userNames.has(value)) || (userId && [record.ownerId, record.creatorId, record.reporterId, record.assigneeId, record.createdById, record.updatedById].some((id) => `${id || ""}` === userId));
    };
    const isProjectRecord = (record) => record.moduleName === "Projects";
    const isTicketRecord = (record) => record.moduleName === "Work" && /task|ticket|bug|request|support|incident|feature|story|improvement/i.test(`${record.type || ""}`) && !/epic|sprint|template|automation|approval/i.test(`${record.type || ""}`);
    const involvedTickets = workspaceRecords.filter((record) => isTicketRecord(record) && isUserInvolved(record));
    const involvedTicketProjectIds = new Set(involvedTickets.map((record) => `${record.projectId || ""}`.trim()).filter(Boolean));
    const involvedTicketProjectRefs = new Set(involvedTickets.map((record) => `${record.projectRef || record.project || record.projectName || ""}`.trim().toLowerCase()).filter(Boolean));
    const projectKeys = new Set();
    workspaceRecords.filter(isProjectRecord).forEach((record) => {
      const explicitMatch = isUserInvolved(record);
      const recordId = `${record.id || ""}`.trim();
      const recordTitle = `${record.title || ""}`.trim().toLowerCase();
      const hasLinkedTicket = (recordId && involvedTicketProjectIds.has(recordId)) || (recordTitle && involvedTicketProjectRefs.has(recordTitle));
      if (explicitMatch || hasLinkedTicket) projectKeys.add(`${record.id || record.title}`);
    });
    const visibleProjectCount = projectClientOptions.filter((option) => option.type === "project").length;
    return {
      projects: visibleProjectCount || projectKeys.size,
      tickets: involvedTickets.length,
    };
  }, [projectClientOptions, workspaceRecords, workspaceUser]);
  const copySidebarEmail = async () => {
    if (!sidebarUserEmail || sidebarUserEmail === "No email") return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(sidebarUserEmail);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = sidebarUserEmail;
        textarea.setAttribute("readonly", "true");
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setSidebarEmailCopied(true);
      window.setTimeout(() => setSidebarEmailCopied(false), 1200);
    } catch (error) {
      setSidebarEmailCopied(false);
    }
  };

  useEffect(() => {
    if (typeof document === "undefined") return;

    const titleBase = activeProjectTitle ? `${activeProjectTitle} | ${workspaceName}` : workspaceName;
    const title = titleBase === "Lyriclious Workspace" ? titleBase : `${titleBase} | Lyriclious Workspace`;
    document.title = title;
    document.querySelector("title")?.replaceChildren(title);

    // Remove the static CRA favicon links so the browser has one authoritative
    // workspace icon rather than choosing the default icon from index.html.
    document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']").forEach((link) => link.remove());
    const addIconLink = (rel, href, id) => {
      const link = document.createElement("link");
      link.id = id;
      link.rel = rel;
      link.href = href;
      if (rel === "icon") link.type = /\.ico(?:$|[?#])/i.test(href) ? "image/x-icon" : "image/png";
      document.head.appendChild(link);
    };
    const cacheKey = encodeURIComponent(`${workspaceInfo?.id || workspaceName}-${activeFavicon}`);
    const faviconHref = activeFavicon.startsWith("data:")
      ? activeFavicon
      : `${activeFavicon}${activeFavicon.includes("?") ? "&" : "?"}workspace-icon=${cacheKey}`;
    addIconLink("icon", faviconHref, "workspace-favicon");
    addIconLink("shortcut icon", faviconHref, "workspace-shortcut-icon");
    addIconLink("apple-touch-icon", activeHeaderIcon || activeFavicon, "workspace-apple-touch-icon");
  }, [activeFavicon, activeHeaderIcon, activeProjectTitle, workspaceInfo?.id, workspaceName]);

  const notificationCount = useMemo(() => {
    const today = getTodayValue();
    const visibleRecords = workspaceRecords.filter((record) => {
      if (!workspaceUser?.name) return true;
      const role = workspaceUser?.role?.toLowerCase() || "";
      return /admin|founder|manager|lead/.test(role) || record.owner === workspaceUser.name;
    });
    const urgentRecords = visibleRecords.filter((record) => record.status === "Blocked" || record.due === today);
    return Math.min(9, urgentRecords.length + Math.min(3, workspaceActivity.length));
  }, [workspaceActivity.length, workspaceRecords, workspaceUser]);

  useEffect(() => {
    if (isProjectIndependentActiveNav) return;
    if (routeProjectId) return;
    if (!selectedProject && projectClientOptions.length) {
      setSelectedProject(projectClientOptions[0].key);
      setRouteProjectId(projectClientOptions[0].routeKey);
    }
  }, [isProjectIndependentActiveNav, projectClientOptions, routeProjectId, selectedProject]);

  useEffect(() => {
    if (isProjectIndependentActiveNav) return;
    if (!selectedProject || !projectClientOptions.length || projectClientOptions.some((option) => option.key === selectedProject)) return;
    if (routeProjectId) {
      if (routeProjectOption && selectedProject !== routeProjectOption.key) setSelectedProject(routeProjectOption.key);
      return;
    }
    setSelectedProject(projectClientOptions[0]?.key || "");
    setRouteProjectId(projectClientOptions[0]?.routeKey || "");
  }, [isProjectIndependentActiveNav, projectClientOptions, routeProjectId, routeProjectOption, selectedProject]);

  const setModuleTab = (moduleName, tab) => {
    setActiveSubtabs((current) => ({ ...current, [moduleName]: tab }));
  };

  const handleUpdateWorkspaceAdministration = async (payload) => {
    const data = await updateWorkspaceAdministration(payload);
    setWorkspaceInfo((current) => ({
      ...(current || {}),
      ...(data.workspace || {}),
      administration: data.administration || current?.administration,
    }));
    setWorkspaceAdministration(data.administration || null);
    prependActivity(data.activity);
    return data;
  };

  const refreshWorkspaceAdministration = async () => {
    setWorkspaceError("");
    try {
      const data = await getWorkspaceAdministration();
      setWorkspaceInfo((current) => ({ ...(current || {}), ...(data.workspace || {}), administration: data.administration || current?.administration }));
      setWorkspaceAdministration(data.administration || null);
      return data;
    } catch (error) {
      setWorkspaceError(error?.response?.data?.message || "Unable to refresh workspace administration.");
      throw error;
    }
  };

  const saveWorkspaceTheme = ({ nextThemeMode, nextColor, delay = 500 }) => {
    if (themeSaveTimer.current) window.clearTimeout(themeSaveTimer.current);
    setThemeSaveStatus("Saving...");
    themeSaveTimer.current = window.setTimeout(async () => {
      try {
        const data = await handleUpdateWorkspaceAdministration({
          branding: {
            themeMode: nextThemeMode,
            colorThemeMode: nextThemeMode,
            primaryColor: nextColor,
          },
        });
        setWorkspaceInfo((current) => ({
          ...(current || {}),
          ...(data.workspace || {}),
          branding: data.workspace?.branding || {
            ...(current?.branding || {}),
            theme_mode: nextThemeMode,
            color_theme_mode: nextThemeMode,
            primary_color: nextColor,
          },
        }));
        setThemeSaveStatus("Saved");
      } catch (error) {
        setThemeSaveStatus(error?.response?.data?.message || "Could not save theme");
      }
    }, delay);
  };

  const updateWorkspaceThemePreference = ({ themeMode: nextThemeMode = themeMode, color = organizationColor, delay = 500 }) => {
    setThemeMode(nextThemeMode);
    setOrganizationColor(color);
    setWorkspaceInfo((current) => ({
      ...(current || {}),
      branding: {
        ...(current?.branding || {}),
        theme_mode: nextThemeMode,
        color_theme_mode: nextThemeMode,
        primary_color: color,
      },
    }));
    saveWorkspaceTheme({ nextThemeMode, nextColor: color, delay });
  };

  const updateWorkspaceAppearancePreference = async (nextAppearanceMode) => {
    if (!["light", "dark", "system"].includes(nextAppearanceMode)) return;
    setAppearanceMode(nextAppearanceMode);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(workspaceAppearanceStorageKey, nextAppearanceMode);
    }
    setThemeSaveStatus("Saving...");
    try {
      const data = await updateWorkspaceAccountPreferences({
        workspace: { appearance_mode: nextAppearanceMode },
      });
      setWorkspaceUser((current) => ({
        ...(current || {}),
        accountPreferences: data.preferences || current?.accountPreferences || {},
      }));
      setThemeSaveStatus("Saved");
    } catch (error) {
      setThemeSaveStatus(error?.response?.data?.message || "Could not save appearance");
    }
  };

  const handleUpdateWorkspaceBranding = async ({
    logoUrl = "",
    logoLongUrl = "",
    logoSquareUrl = "",
    faviconUrl = "",
    faviconIcoUrl = "",
    themeMode: nextThemeMode = themeMode,
    colorThemeMode = nextThemeMode,
    primaryColor = organizationColor,
  }) => {
    setThemeSaveStatus("Saving branding...");
      try {
      const data = await handleUpdateWorkspaceAdministration({
        branding: {
          themeMode: colorThemeMode,
          colorThemeMode,
          primaryColor,
          logoUrl,
          logoLongUrl,
          logoSquareUrl,
          faviconUrl,
          faviconIcoUrl,
        },
      });
      setWorkspaceInfo((current) => ({
        ...(current || {}),
        ...(data.workspace || {}),
        branding: data.workspace?.branding || {
          ...(current?.branding || {}),
          theme_mode: colorThemeMode,
          color_theme_mode: colorThemeMode,
          primary_color: primaryColor,
          logo_url: logoUrl,
          logo_long_url: logoLongUrl,
          logo_square_url: logoSquareUrl || logoUrl,
          favicon_url: faviconUrl,
          favicon_ico_url: faviconIcoUrl || faviconUrl,
        },
      }));
      if (data.workspace?.branding?.color_theme_mode || data.workspace?.branding?.theme_mode) setThemeMode(data.workspace.branding.color_theme_mode || data.workspace.branding.theme_mode);
      if (data.workspace?.branding?.primary_color) setOrganizationColor(data.workspace.branding.primary_color);
      setThemeSaveStatus("Branding saved");
      return data;
    } catch (error) {
      const message = error?.response?.data?.message || "Could not save branding";
      setThemeSaveStatus(message);
      throw error;
    }
  };

  const prependActivity = (activity) => {
    if (!activity) return;
    setWorkspaceActivity((current) => [activity, ...current].slice(0, 40));
  };

  const handleCreateWorkspaceRecord = async (payload, { localError = false } = {}) => {
    setWorkspaceError("");
    try {
      const data = await createWorkspaceRecord(payload);
      setWorkspaceRecords((current) => [data.record, ...current]);
      prependActivity(data.activity);
      return data.record;
    } catch (error) {
      if (!localError) setWorkspaceError(error?.response?.data?.message || "Unable to create Workspace record.");
      throw error;
    }
  };

  const handleUpdateWorkspaceRecord = async (id, payload, { localError = false } = {}) => {
    setWorkspaceError("");
    try {
      const data = await updateWorkspaceRecord(id, payload);
      setWorkspaceRecords((current) =>
        current.map((record) => (record.id === id ? data.record : record))
      );
      prependActivity(data.activity);
      return data.record;
    } catch (error) {
      if (!localError) setWorkspaceError(error?.response?.data?.message || "Unable to update Workspace record.");
      throw error;
    }
  };

  const handleDeleteWorkspaceRecord = async (id) => {
    setWorkspaceError("");
    try {
      const data = await deleteWorkspaceRecord(id);
      setWorkspaceRecords((current) => current.filter((record) => record.id !== id));
      prependActivity(data.activity);
      return data.deletedId;
    } catch (error) {
      setWorkspaceError(error?.response?.data?.message || "Unable to delete Workspace record.");
      throw error;
    }
  };

  const handleProjectClientCreate = async (event) => {
    event.preventDefault();
    const title = projectCreateTitle.trim();
    if (!title) return;
    if (!canCreateProject) {
      setProjectCreateError(canManageProjectClient ? `Free plan allows ${freeProjectLimit} project${freeProjectLimit === 1 ? "" : "s"} for this workspace.` : "Only workspace admins or super admins can add projects.");
      return;
    }

    setProjectCreateSaving(true);
    setProjectCreateError("");
    const type = "project";
    const moduleName = "Projects";
    const tab = "Active";

    try {
      const record = await handleCreateWorkspaceRecord({
        moduleName,
        tab,
        title,
        owner: workspaceUser?.name || "",
        priority: "High",
        status: "Active",
        type,
      });
      closeWorkspaceHelp();
      setSelectedProject(`project:${record.id || title}`);
      setRouteProjectId(record.projectSlug || record.id || title);
      setProjectCreateTitle("");
      setProjectMenuOpen(false);
      setProjectCreateDialogOpen(false);
      setActiveNav(moduleName);
      setDocumentationMode(false);
      setDocumentationOpen(false);
      setModuleTab(moduleName, tab);
    } catch (error) {
      setProjectCreateError(error?.response?.data?.message || "Unable to add this project.");
    } finally {
      setProjectCreateSaving(false);
    }
  };

  const workspaceSettingsProps = {
    themeMode,
    setThemeMode,
    appearanceMode,
    setAppearanceMode,
    updateWorkspaceAppearancePreference,
    organizationColor,
    setOrganizationColor,
    updateWorkspaceThemePreference,
    themeSaveStatus,
    onUpdateWorkspaceAdministration: handleUpdateWorkspaceAdministration,
    canManagePersonalCalendars: Boolean(workspaceUser),
    currentUser: workspaceUser,
    onAccountPreferencesUpdated: (preferences) => {
      setWorkspaceUser((current) => ({
        ...(current || {}),
        accountPreferences: preferences || current?.accountPreferences || {},
      }));
    },
    onOpenLogoutDialog: () => setLogoutDialogOpen(true),
  };

  const handleReportSubmit = async (event) => {
    event.preventDefault();
    const title = reportTitle.trim();
    const description = reportDescription.trim();
    if (!title || !description) {
      setReportError("Title and details are required.");
      return;
    }

    setReportSaving(true);
    setReportError("");
    setReportMessage("");
    try {
      const data = await submitWorkspaceReport({
        report_type: reportType,
        title,
        description,
        page_context: {
          path: window.location.pathname,
          url: window.location.href,
          moduleName: breadcrumbModule,
          tab: breadcrumbTab,
          project: breadcrumbProject,
        },
      });
      setReportMessage(data.message || "Report saved.");
      setReportTitle("");
      setReportDescription("");
      window.setTimeout(() => {
        setReportDialogOpen(false);
        setReportMessage("");
      }, 1200);
    } catch (error) {
      setReportError(error?.response?.data?.message || "Unable to save this report.");
    } finally {
      setReportSaving(false);
    }
  };

  const printDocumentation = () => {
    window.print();
  };

  const renderFunctionalView = () => {
    if (documentationMode) {
      return (
        <DocumentationView
          activeLabel={documentationLabel}
          onActiveTopicChange={setDocumentationLabel}
          onSelectTopic={openDocumentationTopic}
          workView={workView}
          setWorkView={setWorkView}
          workspaceAdministration={workspaceAdministration}
        />
      );
    }

    return (
      <FunctionalWorkspaceView
        activeTab={isGeneralNav ? activeNav : activeModuleTab}
        activity={workspaceActivity}
        currentUser={workspaceUser}
        error={workspaceError}
        isLoading={workspaceLoading}
        liveNow={liveNow}
        moduleName={isGeneralNav ? "Home" : activeNav}
        onCreateRecord={handleCreateWorkspaceRecord}
        onDeleteRecord={handleDeleteWorkspaceRecord}
        onUpdateRecord={handleUpdateWorkspaceRecord}
        onUpdateWorkspaceBranding={handleUpdateWorkspaceBranding}
        onNavigateModule={(moduleName, tabName) => { setDocumentationMode(false); setActiveNav(moduleName); setModuleTab(moduleName, tabName); }}
        activeBoardId={activeBoardId}
        onActiveBoardChange={setActiveBoardId}
        focusedCommentId={focusedCommentId}
        routeRecordId={routeRecordId}
        onRecordRouteChange={(record, commentId = "") => {
          if (!record) {
            setRouteRecordId("");
            setRouteProjectId("");
            setFocusedCommentId("");
            return;
          }
          const projectSlug = getProjectRouteSegment(record, workspaceRecords);
          if (!projectSlug) return;
          setActiveBoardId("");
          setRouteProjectId(projectSlug);
          setRouteRecordId(getRecordRouteSegment(record));
          setFocusedCommentId(commentId);
        }}
        onUpdateWorkspaceAdministration={handleUpdateWorkspaceAdministration}
        onRefreshWorkspaceAdministration={refreshWorkspaceAdministration}
        moduleAccess={moduleStateMap[activeNav] || null}
        moduleTabs={runtimeModuleTabs[activeNav] || []}
        peopleSearchQuery={peopleDirectorySearchQuery}
        records={isProjectIndependentActiveNav ? workspaceRecords : scopedWorkspaceRecords}
        onOverviewRecordsLoaded={cacheOverviewRecords}
        selectedProjectName={isProjectIndependentActiveNav ? "" : selectedProjectTitle}
        selectedProjectId={isProjectIndependentActiveNav ? "" : selectedProjectRecordId}
        selectedProjectSlug={isProjectIndependentActiveNav ? "" : (selectedProjectOption?.routeKey || "")}
        selectedProjectBranding={isProjectIndependentActiveNav ? {} : selectedProjectBranding}
        onProjectSlugChange={setRouteProjectId}
        setActiveTab={(tab) => setModuleTab(activeNav, tab)}
        settingsProps={workspaceSettingsProps}
        workspaceAdministration={workspaceAdministration}
        workspaceInfo={workspaceInfo}
      />
    );
  };

  const openDocumentationTopic = (item) => {
    setDocumentationLabel(item);
    setDocumentationMode(true);
    setDocumentationOpen(false);
    window.setTimeout(() => {
      document.getElementById(documentationId(item))?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
  };

  const scopedWorkspaceRecords = useMemo(() => {
    if (documentationMode || isGeneralNav || isProjectIndependentActiveNav || !selectedProjectOption) return workspaceRecords;
    return workspaceRecords.filter((record) => {
      const recordType = `${record.type || ""}`.toLowerCase();
      const recordProjectId = `${record.projectId || ""}`;
      const isProjectRecord = record.moduleName === "Projects" || recordType.includes("project");
      const recordProjectKeys = [
        record.projectRef,
        record.projectSlug,
        isProjectRecord ? record.title : "",
      ].map((value) => `${value || ""}`.trim().toLowerCase()).filter(Boolean);
      const matchesSelectedProjectContext = Boolean(
        (selectedProjectRecordId && recordProjectId === selectedProjectRecordId) ||
        recordProjectKeys.some((key) => selectedProjectMatchKeys.has(key))
      );
      if (record.moduleName === "People") return true;
      if (isProjectRecord) {
        return record.id === selectedProjectRecordId || matchesSelectedProjectContext;
      }
      return matchesSelectedProjectContext;
    });
  }, [documentationMode, isGeneralNav, isProjectIndependentActiveNav, selectedProjectMatchKeys, selectedProjectOption, selectedProjectRecordId, workspaceRecords]);
  const breadcrumbProject = selectedProjectOption?.displayTitle || workspaceName;
  const isWorkspaceWideSection = isProjectIndependentActiveNav && !documentationMode;
  const breadcrumbModule = documentationMode ? "Documentation" : isGeneralNav ? "General" : activeNav;
  const breadcrumbTab = documentationMode ? documentationLabel : isGeneralNav ? activeNav : activeModuleTab;
  const navigateToTab = (tab) => {
    if (isGeneralNav && generalItems.some((item) => item.label === tab)) {
      closeWorkspaceHelp();
      setRouteRecordId("");
      setFocusedCommentId("");
      setActiveNav(tab);
      setDocumentationMode(false);
      setDocumentationOpen(false);
      return;
    }
    if (documentationMode || !tab || !(runtimeModuleTabs[activeNav] || []).some((item) => item.name === tab)) return;
    closeWorkspaceHelp();
    if (tab !== activeModuleTab) {
      setRouteRecordId("");
      setFocusedCommentId("");
    }
    setDocumentationMode(false);
    setDocumentationOpen(false);
    setModuleTab(activeNav, tab);
  };

  const showSidebarTooltip = (event, label, locked = false, { showWhenExpanded = false } = {}) => {
    if ((sidebarExpanded && !showWhenExpanded) || window.innerWidth <= 760) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setSidebarTooltip({
      label,
      locked,
      left: rect.right + 12,
      top: rect.top + rect.height / 2,
    });
  };

  const hideSidebarTooltip = () => {
    setSidebarTooltip(null);
  };

  const confirmLogout = async () => {
    setLogoutLoading(true);
    setLogoutError("");

    try {
      await logoutWorkspaceSession();
      // Do not reload Workspace after an intentional logout. Its remembered
      // slug would otherwise turn the expected signed-out state into a
      // misleading “unable to restore session” error screen.
      window.location.replace(getLyricliousPublicUrl("/login"));
    } catch (error) {
      setLogoutError(error?.response?.data?.message || "We could not sign you out of Lyriclious. Please try again.");
      setLogoutLoading(false);
    }
  };

  const renderDocumentationOnly = () => {
    const signInUrl = getLyricliousLoginUrl();
    const signUpUrl = getLyricliousRegisterUrl();
    const openPublicDocumentation = () => {
      setDocumentationLabel("Dashboard");
      setDocumentationMode(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const updateOnboardingPreference = (onboardingPreference) => {
      captureWorkspaceOnboardingLead({ onboardingPreference, pagePath: window.location.href }).catch(() => {});
    };
    return (
      <div className={`workspace-public-site public-theme-${publicTheme}`} style={themeStyle}>
        <header className="workspace-public-header">
          <button
            className="workspace-public-brand"
            onClick={() => {
              setDocumentationMode(false);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            type="button"
          >
            <img
              alt="Lyriclious"
              height="36"
              src={DEFAULT_WORKSPACE_ICON}
              width="36"
            />
            <div>
              <strong>Lyriclious Workspace</strong>
              <small>Company operating system</small>
            </div>
          </button>
          <nav aria-label="Public Workspace navigation">
            <button
              className={!documentationMode ? "active" : ""}
              onClick={() => setDocumentationMode(false)}
              type="button"
            >
              Product
            </button>
            <button
              className={documentationMode ? "active" : ""}
              onClick={openPublicDocumentation}
              type="button"
            >
              Documentation
            </button>
          </nav>
          <div className="workspace-public-header-actions">
            <button
              aria-label={`Switch to ${publicTheme === "dark" ? "light" : "dark"} mode`}
              className="workspace-public-theme-toggle"
              onClick={togglePublicTheme}
              title={`Switch to ${publicTheme === "dark" ? "light" : "dark"} mode`}
              type="button"
            >
              {publicTheme === "dark" ? (
                <LightModeOutlined fontSize="small" />
              ) : (
                <DarkModeOutlined fontSize="small" />
              )}
              <span>{publicTheme === "dark" ? "Light" : "Dark"}</span>
            </button>
            {!workspaceOnboardingPrompt ? (
              <a className="workspace-public-header-sign-in" href={signInUrl}>
                <Login fontSize="small" />
                Sign in with Lyriclious
              </a>
            ) : null}
          </div>
        </header>

        {workspaceOnboardingPrompt ? <div className="workspace-onboarding-toast" role="status">You are signed in to Lyriclious, but you are not part of any active Workspace.</div> : null}

        {!documentationMode ? (
          <WorkspaceLanding
            onboardingPrompt={workspaceOnboardingPrompt}
            onOnboardingChoice={updateOnboardingPreference}
            onOpenDocumentation={openPublicDocumentation}
            signInUrl={signInUrl}
            signUpUrl={signUpUrl}
          />
        ) : (
          <div className="workspace-public-shell">
            <aside className="workspace-public-sidebar">
              <div className="public-doc-menu">
                <div className="public-doc-heading"><Description fontSize="small" /><span>Documentation</span></div>
                <nav className="public-doc-list" aria-label="Workspace documentation">
                  {documentationItems.filter((item) => item !== "Product Blueprint").map((item) => <button aria-current={documentationLabel === item ? "page" : undefined} className={documentationLabel === item ? "active" : ""} key={item} onClick={() => { setDocumentationLabel(item); window.setTimeout(() => document.getElementById(documentationId(item))?.scrollIntoView({ behavior: "smooth", block: "start" }), 0); }} type="button">{item}</button>)}
                </nav>
              </div>
            </aside>
            <main className="workspace-public-docs">
              <header className="workspace-public-docs-header">
                <div><span>Workspace guide</span><h1>Understand how your team will work</h1><p>A concise guide to Lyriclious Workspace—delivery, people, projects, access, and client collaboration. Sign in when your workspace invitation is active.</p></div>
                <button className="secondary-action" onClick={printDocumentation} type="button"><Description fontSize="small" />Print / Save PDF</button>
              </header>
              <div className="workspace-public-docs-note">
                <p>{workspaceError || "Documentation is publicly available; live workspace actions require sign-in and access approval."}</p>
                {workspaceError ? <button className="secondary-action" onClick={() => { setWorkspaceLoading(true); setWorkspaceError(""); setWorkspaceLoadAttempt((attempt) => attempt + 1); }} type="button">Try Workspace again</button> : null}
              </div>
              <DocumentationView activeLabel={documentationLabel} hiddenTopics={["Product Blueprint"]} onActiveTopicChange={setDocumentationLabel} onSelectTopic={(item) => { setDocumentationLabel(item); window.setTimeout(() => document.getElementById(documentationId(item))?.scrollIntoView({ behavior: "smooth", block: "start" }), 0); }} setWorkView={setWorkView} showIndex={false} workView={workView} />
            </main>
          </div>
        )}
      </div>
    );
  };

  const renderSidebarButton = (item, section = "workspace") => {
    const Icon = item.icon;
    const isActive = !documentationMode && activeNav === item.label;
    const isGeneralItem = section === "general";
    const moduleConfig = moduleStateMap[item.label];
    const isPremium = moduleConfig?.premium === true;
    const isPrivateOwnerDevelopmentModule = PRIVATE_OWNER_MODULES.has(item.label) && moduleConfig?.enabled === false;
    const isComingSoon = item.comingSoon === true || moduleConfig?.comingSoon === true || isPrivateOwnerDevelopmentModule;
    const canOpenComingSoon = !isComingSoon || hasDeferredModuleAccess || (isPrivateOwnerDevelopmentModule && hasPrivateOwnerModuleAccess);
    return (
      <button
        aria-disabled={!canOpenComingSoon || undefined}
        aria-label={isComingSoon ? `${item.label} — feature under development${hasDeferredModuleAccess ? "; access enabled" : ""}` : item.label}
        className={`${isActive ? "active" : ""} ${isPremium ? "premium" : ""} ${isComingSoon ? "coming-soon" : ""}`.trim()}
        onClick={() => {
          hideSidebarTooltip();
          closeWorkspaceHelp();
          if (!canOpenComingSoon) return;
          if (item.label !== "Work") {
            setRouteRecordId("");
            setFocusedCommentId("");
          }
          setActiveNav(item.label);
          setDocumentationMode(false);
          setDocumentationOpen(false);
          if (!isGeneralItem && runtimeModuleTabs[item.label]?.length && !activeSubtabs[item.label]) {
            setModuleTab(item.label, runtimeModuleTabs[item.label][0].name);
          }
        }}
        onBlur={hideSidebarTooltip}
        onFocus={(event) => showSidebarTooltip(event, item.label, isComingSoon)}
        onMouseEnter={(event) => showSidebarTooltip(event, item.label, isComingSoon)}
        onMouseLeave={hideSidebarTooltip}
        type="button"
      >
        <Icon fontSize="small" />
        <span>{item.displayLabel || item.label}</span>
        {isComingSoon && sidebarExpanded ? <small aria-hidden="true" className="nav-item-lock"><LockOutlined fontSize="inherit" /></small> : null}
        {isPremium ? <small className="nav-item-pill"><LockOutlined fontSize="inherit" />Premium</small> : null}
        {item.label === "Notifications" && notificationCount > 0 ? (
          <em className="nav-badge">{notificationCount}</em>
        ) : null}
      </button>
    );
  };

  if (workspaceLoading) {
    return (
      <main aria-busy="true" aria-live="polite" className="workspace-loading-screen" style={themeStyle}>
        <div className="workspace-loading-card">
          <div aria-hidden="true" className="workspace-loading-mark">
            <img src={DEFAULT_WORKSPACE_WORDMARK} alt="" />
          </div>
          <div className="workspace-loading-spinner" />
          <div>
            <h1>Preparing your workspace</h1>
            <p>Checking your session, permissions, and latest workspace data.</p>
          </div>
        </div>
      </main>
    );
  }

  const hasWorkspaceContext = Boolean(getWorkspaceSlugFromPath() || getRememberedWorkspaceSlug());
  const shouldShowWorkspaceShowcase =
    workspaceOnboardingPrompt &&
    (
      !workspaceError ||
      /workspace access is not enabled|not part of an active workspace|not part of any active workspace/i.test(workspaceError)
    );

  if (!workspaceUser && hasWorkspaceContext && !shouldShowWorkspaceShowcase) {
    return (
      <main aria-live="polite" className="workspace-loading-screen" style={themeStyle}>
        <div className="workspace-loading-card">
          <div aria-hidden="true" className="workspace-loading-mark">
            <img src={DEFAULT_WORKSPACE_WORDMARK} alt="" />
          </div>
          <div>
            <h1>Unable to restore workspace session</h1>
            <p>{workspaceError || "Workspace is retrying your session and workspace access."}</p>
          </div>
          <div className="workspace-dialog-actions">
            <button className="secondary-action" onClick={() => { setWorkspaceLoading(true); setWorkspaceError(""); setWorkspaceLoadAttempt((attempt) => attempt + 1); }} type="button">
              Try again
            </button>
            <a className="primary-action" href={getLyricliousLoginUrl()}>
              Sign in again
            </a>
          </div>
        </div>
      </main>
    );
  }

  if (!workspaceUser) {
    return renderDocumentationOnly();
  }

  return (
    <div className={`workspace-shell workspace-theme-${activeAppearanceMode} ${sidebarExpanded ? "sidebar-expanded" : ""}`} style={themeStyle}>
      <aside className="sidebar app-rail">
        <button
          aria-label={sidebarExpanded ? "Minimize sidebar" : "Expand sidebar"}
          className="sidebar-toggle"
          onClick={() => setSidebarExpanded((value) => !value)}
          type="button"
        >
          {sidebarExpanded ? <KeyboardArrowLeft fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
          <span>{sidebarExpanded ? "Minimize" : "Expand"}</span>
        </button>

        <section className="sidebar-user-identity" aria-label="Signed in user">
          <span className="sidebar-user-summary">
            <span className="sidebar-user-avatar" aria-hidden="true">
              {sidebarUserAvatar ? <img src={sidebarUserAvatar} alt="" /> : sidebarUserInitials}
            </span>
            <span className="sidebar-user-copy">
              <strong>{sidebarUserName}</strong>
              {workspaceUser?.role ? <em>{workspaceUser.role}</em> : null}
            </span>
          </span>
          <span className="sidebar-user-email-row">
            <small>{sidebarUserEmail}</small>
            {sidebarUserEmail !== "No email" ? <button aria-label={`Copy ${sidebarUserEmail}`} onClick={copySidebarEmail} title={sidebarEmailCopied ? "Copied" : "Copy email"} type="button">{sidebarEmailCopied ? <CheckOutlined fontSize="inherit" /> : <ContentCopyOutlined fontSize="inherit" />}</button> : null}
          </span>
          <span className="sidebar-user-stats" aria-label="User workspace involvement">
            <span><strong>{sidebarUserStats.projects}</strong><small>Projects</small></span>
            <span><strong>{sidebarUserStats.tickets}</strong><small>Tickets</small></span>
          </span>
        </section>

        <nav className="sidebar-nav" aria-label="Workspace navigation">
          {personalNavItems.length ? <section className="sidebar-nav-section" aria-label="Personal navigation">
            <span className="sidebar-section-label">My space</span>
            <div className="sidebar-nav-group">
              {personalNavItems.map((item) => <React.Fragment key={item.label}>{renderSidebarButton(item, "personal")}</React.Fragment>)}
            </div>
          </section> : null}
          {clientNavItems.length ? <section className="sidebar-nav-section" aria-label="Client navigation">
            <span className="sidebar-section-label">Client space</span>
            <div className="sidebar-nav-group">
              {clientNavItems.map((item) => <React.Fragment key={item.label}>{renderSidebarButton(item, "personal")}</React.Fragment>)}
            </div>
          </section> : null}
          {workspaceNavItems.length ? <section className="sidebar-nav-section" aria-label="Workspace modules">
            <span className="sidebar-section-label">Workspace</span>
            <div className="sidebar-nav-group">
          {workspaceNavItems.map((item) => {
            return (
              <React.Fragment key={item.label}>
              {renderSidebarButton(item)}
              </React.Fragment>
            );
          })}
            </div>
          </section> : null}
        </nav>

        <div className="sidebar-secondary" aria-label="Secondary workspace navigation">
          <button
            aria-label="Settings"
            className={`secondary-nav-link ${activeNav === "Settings" && !documentationMode ? "active" : ""}`}
            onClick={() => {
              hideSidebarTooltip();
              closeWorkspaceHelp();
              setDocumentationMode(false);
              setDocumentationOpen(false);
              setActiveNav("Settings");
              setModuleTab("Settings", "Account");
            }}
            onBlur={hideSidebarTooltip}
            onFocus={(event) => showSidebarTooltip(event, "Settings", false, { showWhenExpanded: true })}
            onMouseEnter={(event) => showSidebarTooltip(event, "Settings", false, { showWhenExpanded: true })}
            onMouseLeave={hideSidebarTooltip}
            type="button"
          >
            <SettingsIcon fontSize="small" />
            <span>Settings</span>
          </button>
          <button
            aria-label="Support"
            className="secondary-nav-link"
            onBlur={hideSidebarTooltip}
            onFocus={(event) => showSidebarTooltip(event, "Support", false, { showWhenExpanded: true })}
            onMouseEnter={(event) => showSidebarTooltip(event, "Support", false, { showWhenExpanded: true })}
            onMouseLeave={hideSidebarTooltip}
            type="button"
          >
            <SupportAgent fontSize="small" />
            <span>Support</span>
          </button>
          <button
            aria-label="Documentation"
            className={`secondary-nav-link ${documentationMode ? "active" : ""}`}
            onClick={() => {
              hideSidebarTooltip();
              closeWorkspaceHelp();
              setDocumentationLabel("Product Blueprint");
              setDocumentationMode(true);
              setDocumentationOpen(false);
              window.setTimeout(() => {
                document.getElementById(documentationId("Product Blueprint"))?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }, 80);
            }}
            onBlur={hideSidebarTooltip}
            onFocus={(event) => showSidebarTooltip(event, "Documentation", false, { showWhenExpanded: true })}
            onMouseEnter={(event) => showSidebarTooltip(event, "Documentation", false, { showWhenExpanded: true })}
            onMouseLeave={hideSidebarTooltip}
            type="button"
          >
            <Description fontSize="small" />
            <span>Documentation</span>
          </button>
          <button
            aria-label="Logout"
            className="secondary-nav-link"
            onClick={() => {
              hideSidebarTooltip();
              closeWorkspaceHelp();
              setLogoutError("");
              setLogoutDialogOpen(true);
            }}
            onBlur={hideSidebarTooltip}
            onFocus={(event) => showSidebarTooltip(event, "Logout", false, { showWhenExpanded: true })}
            onMouseEnter={(event) => showSidebarTooltip(event, "Logout", false, { showWhenExpanded: true })}
            onMouseLeave={hideSidebarTooltip}
            type="button"
          >
            <Logout fontSize="small" />
            <span>Logout</span>
          </button>
          <button
            aria-label="Report issue or feature"
            className="sidebar-report-action"
            onClick={() => {
              hideSidebarTooltip();
              setReportDialogOpen(true);
              setReportError("");
              setReportMessage("");
            }}
            onBlur={hideSidebarTooltip}
            onFocus={(event) => showSidebarTooltip(event, "Report issue or feature")}
            onMouseEnter={(event) => showSidebarTooltip(event, "Report issue or feature")}
            onMouseLeave={hideSidebarTooltip}
            type="button"
          >
            <BugReport fontSize="small" />
            <span>Report issue</span>
          </button>
        </div>

      </aside>
      {sidebarTooltip && (
        <div
          className="sidebar-floating-tooltip"
          role="tooltip"
          style={{ left: sidebarTooltip.left, top: sidebarTooltip.top }}
        >
          <span>{sidebarTooltip.label}</span>
          {sidebarTooltip.locked ? <LockOutlined aria-label="Locked" fontSize="inherit" /> : null}
        </div>
      )}
      {instantTooltip ? (
        <div
          className="workspace-floating-tooltip"
          role="tooltip"
          style={{ left: instantTooltip.left, top: instantTooltip.top }}
        >
          <span>{instantTooltip.label}</span>
        </div>
      ) : null}

      <main className="workspace-main">
        <header className="topbar module-topbar">
          <div className="workspace-crumb-header">
            {activeHeaderIcon ? (
              <img className="topbar-brand-icon" alt="" src={activeHeaderIcon} />
            ) : (
              <div aria-hidden="true" className="topbar-brand-icon is-initial">
                {(!isProjectIndependentActiveNav && breadcrumbProject ? breadcrumbProject : workspaceInitial).trim().charAt(0).toUpperCase() || workspaceInitial}
              </div>
            )}
            <div className="workspace-crumb-copy">
              <nav className="workspace-breadcrumbs" aria-label="Workspace location">
              {isWorkspaceWideSection ? (
                <span className="breadcrumb-workspace-name">{workspaceName}</span>
              ) : (
              <div className="breadcrumb-project-control" ref={projectSwitcherRef}>
                <button
                  aria-expanded={projectMenuOpen}
                  aria-haspopup="menu"
                  className="breadcrumb-project-trigger"
                  onClick={() => {
                    closeWorkspaceHelp();
                    setProjectMenuOpen((value) => !value);
                  }}
                  type="button"
                >
                  {breadcrumbProject}
                  <KeyboardArrowDown fontSize="small" />
                </button>
                {projectMenuOpen ? (
                  <div className="project-switcher-menu" role="menu">
                    <div className="project-switcher-list">
                      {projectClientOptions.map((option) => (
                        <div className={`project-switcher-row ${selectedProjectOption?.key === option.key ? "active" : ""}`.trim()} key={option.key}>
                          <button
                            className="project-switcher-select"
                            onClick={() => {
                              setSelectedProject(option.key);
                              setRouteProjectId(option.routeKey);
                              setRouteRecordId("");
                              setFocusedCommentId("");
                              setProjectMenuOpen(false);
                            }}
                            type="button"
                          >
                            <span className="project-switcher-icon" aria-hidden="true">
                              {option.icon ? <img src={option.icon} alt="" /> : <FolderOutlined fontSize="small" />}
                            </span>
                            <span className="project-switcher-copy">
                              <span>{option.displayTitle}</span>
                              <small>{option.type}</small>
                            </span>
                          </button>
                          {option.type === "project" && !option.virtualProjectContext ? <button aria-label={`Open overview for ${option.displayTitle}`} className="project-switcher-settings" onClick={() => { setSelectedProject(option.key); setRouteRecordId(""); setFocusedCommentId(""); setRouteProjectId(option.routeKey); setActiveNav("Projects"); setModuleTab("Projects", "Overview"); setProjectMenuOpen(false); }} title={`Open ${option.displayTitle} overview`} type="button"><SettingsIcon fontSize="small" /></button> : null}
                        </div>
                      ))}
                      {!projectClientOptions.length ? (
                        <p>No project or client yet.</p>
                      ) : null}
                    </div>
                    <div className="project-switcher-create">
                      <span>
                        {!canManageProjectClient
                          ? "Only admins or super admins can add projects."
                          : canCreateProject
                          ? "Create a delivery project from here:"
                          : `Project limit reached (${projectUsageCount}/${freeProjectLimit}).`}
                      </span>
                      <div className="project-switcher-create-links">
                        <button
                          disabled={!canCreateProject || projectCreateSaving || workspaceLoading}
                          onClick={() => {
                            setProjectCreateTitle("");
                            setProjectCreateError("");
                            setProjectMenuOpen(false);
                            setProjectCreateDialogOpen(true);
                          }}
                          type="button"
                        >
                          Add project
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              )}
              {breadcrumbModule ? (
                <>
                  <span className="breadcrumb-separator">/</span>
                  <button
                    className="breadcrumb-link section"
                    onClick={() => {
                      if (documentationMode || isGeneralNav) return;
                      setRouteRecordId("");
                      setFocusedCommentId("");
                      setModuleTab(activeNav, runtimeModuleTabs[activeNav]?.[0]?.name || moduleTabs[activeNav]?.[0] || "Overview");
                    }}
                    type="button"
                  >
                    {breadcrumbModule}
                  </button>
                </>
              ) : null}
              {breadcrumbTab ? (
                <>
                  <span className="breadcrumb-separator">/</span>
                  <button
                    className="breadcrumb-link current"
                    onClick={() => navigateToTab(breadcrumbTab)}
                    type="button"
                  >
                    {breadcrumbTab}
                  </button>
                </>
              ) : null}
              </nav>
            </div>
          </div>
          {!documentationMode ? (
            <div className="workspace-search-toolbar" ref={globalSearchContainerRef}>
            <div className="global-workspace-search">
              <Search aria-hidden="true" fontSize="small" />
              <input
                aria-controls="workspace-global-search-results"
                aria-label="Search workspace"
                onChange={(event) => setGlobalSearchQuery(event.target.value)}
                onFocus={() => setGlobalSearchOpen(true)}
                placeholder="Search"
                ref={globalSearchRef}
                type="search"
                value={globalSearchQuery}
              />
              <kbd>{searchShortcutLabel}</kbd>
                {globalSearchOpen ? (
                  <div className="global-workspace-search-results" id="workspace-global-search-results" role="listbox">
                    <div className="global-search-results-list">
                      <div className="global-search-tabs"><button className="active" type="button">Workspace</button><button type="button">Navigate</button></div>
                      <p className="global-search-result-count">Results · {globalSearchResults.length}</p>
                      {globalSearchResults.map((result) => (
                        <button aria-selected="false" key={result.id} onMouseDown={(event) => event.preventDefault()} onClick={() => openGlobalSearchResult(result)} role="option" type="button">
                          <span className="global-search-result-icon"><Search fontSize="small" /></span>
                          <span><strong>{result.title}</strong><small>{result.detail}</small></span>
                          <em>{result.kind}</em>
                        </button>
                      ))}
                      {!globalSearchResults.length ? <p>No workspace results found.</p> : null}
                      <div className="global-search-shortcuts"><span>Go to:</span>{globalSearchShortcuts.map((item) => <button key={item.label} onClick={() => openGlobalSearchResult(item)} type="button">{item.label}</button>)}</div>
                    </div>
                    <aside className="global-search-filters">
                      <p>Last updated</p>
                      <div className="global-filter-chips">{[["any", "Any time"], ["today", "Today"], ["week", "Past 7 days"], ["month", "Past 30 days"], ["year", "Past year"]].map(([value, label]) => <button className={globalSearchPeriod === value ? "active" : ""} key={value} onClick={() => setGlobalSearchPeriod(value)} type="button">{label}</button>)}</div>
                      <p>Filter by assignee</p>
                      <div className="global-filter-checks">{globalSearchAssigneesList.map((person) => <label key={person}><input checked={globalSearchAssignees.includes(person)} onChange={() => setGlobalSearchAssignees((current) => current.includes(person) ? current.filter((item) => item !== person) : [...current, person])} type="checkbox" />{person}</label>)}</div>
                      <p>Filter by reporter</p>
                      <div className="global-filter-checks"><label><input checked={globalSearchReportedByMe} onChange={(event) => setGlobalSearchReportedByMe(event.target.checked)} type="checkbox" />Reported by me</label></div>
                      <p>Filter by status</p>
                      <div className="global-filter-checks global-filter-checks-inline">{[["open", "Open"], ["done", "Done"]].map(([value, label]) => <label key={value}><input checked={globalSearchStatuses.includes(value)} onChange={() => setGlobalSearchStatuses((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value])} type="checkbox" />{label}</label>)}</div>
                      <p>Filter by label</p>
                      <select aria-label="Filter by label" className="global-filter-select" onChange={(event) => setGlobalSearchLabel(event.target.value)} value={globalSearchLabel}><option value="">Select label</option>{globalSearchLabelsList.map((label) => <option key={label} value={label}>{label}</option>)}</select>
                      <button className="global-feedback-link" onClick={() => { setReportType("feature"); setReportDialogOpen(true); setGlobalSearchOpen(false); }} type="button">Give feedback</button>
                      {(globalSearchAssignees.length || globalSearchPeriod !== "any" || globalSearchReportedByMe || globalSearchStatuses.length || globalSearchLabel) ? <button className="global-filter-clear" onClick={() => { setGlobalSearchPeriod("any"); setGlobalSearchAssignees([]); setGlobalSearchReportedByMe(false); setGlobalSearchStatuses([]); setGlobalSearchLabel(""); }} type="button">Clear filters</button> : null}
                    </aside>
                    <button className="global-search-view-all" onClick={() => openGlobalSearchResult({ moduleName: "Work", tabName: "Overview" })} type="button"><Search fontSize="small" />View all work items<span aria-hidden="true">↵</span></button>
                  </div>
                ) : null}
            </div>
            <button className="workspace-search-create" onClick={() => { setQuickTicket({ title: "", description: "", priority: "Medium" }); setQuickTicketOpen(true); }} type="button">
              <span aria-hidden="true">＋</span> Create
            </button>
            </div>
          ) : null}
          <div className="topbar-actions">
            {documentationMode ? (
              <button className="secondary-action" onClick={printDocumentation} type="button">
                <Description fontSize="small" />
                Print / Save PDF
              </button>
            ) : null}
            {!documentationMode ? <>
              <button
                aria-label={activeAppearanceMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                className="topbar-icon-action"
                onClick={() => updateWorkspaceAppearancePreference(activeAppearanceMode === "dark" ? "light" : "dark")}
                title={activeAppearanceMode === "dark" ? "Light mode" : "Dark mode"}
                type="button"
              >
                {activeAppearanceMode === "dark" ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
              </button>
              <button aria-label="Open time record" className="topbar-icon-action" onClick={() => { setActiveNav("People"); setModuleTab("People", "Attendance"); }} title="Time record" type="button"><Timer fontSize="small" /></button>
              <details className="topbar-more-menu topbar-notifications">
                <summary aria-label="Notifications" title="Notifications"><NotificationsNone fontSize="small" />{workspaceActivity.length ? <i>{Math.min(workspaceActivity.length, 9)}</i> : null}</summary>
                <div><span>Notifications</span>{workspaceActivity.slice(0, 5).map((item) => <button key={item.id} onClick={() => { if (moduleStateMap[item.moduleName]?.enabled === false) return; setActiveNav(item.moduleName || "Home"); if ((runtimeModuleTabs[item.moduleName] || []).some((tab) => tab.name === item.tabName)) setModuleTab(item.moduleName, item.tabName); }} type="button"><History fontSize="small" />{item.message}</button>)}{!workspaceActivity.length ? <p>No new activity.</p> : null}</div>
              </details>
              <button aria-label="My work" className="topbar-icon-action" onClick={() => { setActiveNav("Home"); setModuleTab("Home", "My Work"); }} title="My work" type="button"><TaskAlt fontSize="small" /></button>
              <details className="topbar-more-menu">
                <summary aria-label="More workspace options" title="More"><MoreHoriz fontSize="small" /></summary>
                <div>
                  <span className="mobile-topbar-action">Actions</span>
                  <button className="mobile-topbar-action" onClick={() => updateWorkspaceAppearancePreference(activeAppearanceMode === "dark" ? "light" : "dark")} type="button">
                    {activeAppearanceMode === "dark" ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
                    {activeAppearanceMode === "dark" ? "Light mode" : "Dark mode"}
                  </button>
                  <button className="mobile-topbar-action" onClick={() => { setQuickTicket({ title: "", description: "", priority: "Medium" }); setQuickTicketOpen(true); }} type="button">
                    <TaskAlt fontSize="small" />
                    Create ticket
                  </button>
                  <button className="mobile-topbar-action" onClick={() => { setActiveNav("People"); setModuleTab("People", "Attendance"); }} type="button">
                    <Timer fontSize="small" />
                    Time record
                  </button>
                  <button className="mobile-topbar-action" onClick={() => { setActiveNav("Home"); setModuleTab("Home", "My Work"); }} type="button">
                    <TaskAlt fontSize="small" />
                    My work
                  </button>
                  <span className="mobile-topbar-action">Notifications</span>
                  {workspaceActivity.slice(0, 3).map((item) => (
                    <button
                      className="mobile-topbar-action"
                      key={`mobile-${item.id}`}
                      onClick={() => {
                        if (moduleStateMap[item.moduleName]?.enabled === false) return;
                        setActiveNav(item.moduleName || "Home");
                        if ((runtimeModuleTabs[item.moduleName] || []).some((tab) => tab.name === item.tabName)) setModuleTab(item.moduleName, item.tabName);
                      }}
                      type="button"
                    >
                      <NotificationsNone fontSize="small" />
                      {item.message}
                    </button>
                  ))}
                  {!workspaceActivity.length ? <p className="mobile-topbar-action">No new activity.</p> : null}
                  <span>Recent visits</span>
                  <button onClick={() => { setActiveNav("Work"); setModuleTab("Work", "Overview"); }} type="button"><History fontSize="small" />Work overview</button>
                  <button onClick={() => { setActiveNav("People"); setModuleTab("People", "Directory"); }} type="button"><History fontSize="small" />People directory</button>
                  <button onClick={() => { setActiveNav("Home"); setModuleTab("Home", "Approvals"); }} type="button"><History fontSize="small" />Approvals</button>
                </div>
              </details>
            </> : null}
          </div>
        </header>

        <div className="main-functionality-canvas" aria-label="Workspace functionality canvas">
          {renderFunctionalView()}
        </div>
      </main>
      {projectCreateDialogOpen ? (
        <div className="workspace-dialog-backdrop" role="presentation">
          <form className="workspace-create-dialog" onSubmit={handleProjectClientCreate}>
            <header>
              <div>
                <span>Project</span>
                <h2>Add project</h2>
              </div>
              <button
                aria-label="Close"
                onClick={() => {
                  setProjectCreateDialogOpen(false);
                  setProjectCreateError("");
                }}
                type="button"
              >
                Close
              </button>
            </header>
            <label>
              Name
              <input
                autoFocus
                disabled={!canCreateProject || projectCreateSaving}
                onChange={(event) => setProjectCreateTitle(event.target.value)}
                placeholder="Project name"
                value={projectCreateTitle}
              />
            </label>
            <p className="report-context-note">
              Client companies stay in CRM. Projects here are delivery engagements, and clients can later be linked to those projects for dashboard access and issue raising.
            </p>
            {projectCreateError ? <p>{projectCreateError}</p> : null}
            <div className="workspace-dialog-actions">
              <button
                onClick={() => {
                  setProjectCreateDialogOpen(false);
                  setProjectCreateError("");
                }}
                type="button"
              >
                Cancel
              </button>
              <button disabled={!canCreateProject || projectCreateSaving || workspaceLoading} type="submit">
                {projectCreateSaving ? <WorkspaceInlineLoader compact label="Saving" /> : "Create project"}
              </button>
            </div>
          </form>
        </div>
      ) : null}
      {quickTicketOpen ? <div className="workspace-dialog-backdrop" role="presentation"><form className="workspace-create-dialog" onSubmit={async (event) => { event.preventDefault(); if (!quickTicket.title.trim()) return; setQuickTicketSaving(true); try { await handleCreateWorkspaceRecord({ moduleName: "Work", tab: "Tickets", type: "Ticket", title: quickTicket.title.trim(), description: quickTicket.description.trim(), priority: quickTicket.priority, status: "Idea", owner: "", projectRef: selectedProjectTitle, projectId: selectedProjectRecordId }); setQuickTicketOpen(false); } finally { setQuickTicketSaving(false); } }}><header><div><span>Work</span><h2>Create ticket</h2></div><button aria-label="Close" onClick={() => setQuickTicketOpen(false)} type="button">Close</button></header><label>Title<input autoFocus disabled={quickTicketSaving} onChange={(event) => setQuickTicket((current) => ({ ...current, title: event.target.value }))} placeholder="What needs to be done?" value={quickTicket.title} /></label><label>Description<textarea disabled={quickTicketSaving} onChange={(event) => setQuickTicket((current) => ({ ...current, description: event.target.value }))} placeholder="Add useful context" rows="3" value={quickTicket.description} /></label><label>Priority<select disabled={quickTicketSaving} onChange={(event) => setQuickTicket((current) => ({ ...current, priority: event.target.value }))} value={quickTicket.priority}><option>Low</option><option>Medium</option><option>High</option><option>Urgent</option></select></label><div className="workspace-dialog-actions"><button onClick={() => setQuickTicketOpen(false)} type="button">Cancel</button><button disabled={quickTicketSaving || !quickTicket.title.trim()} type="submit">{quickTicketSaving ? "Creating…" : "Create ticket"}</button></div></form></div> : null}
      {reportDialogOpen ? (
        <div className="workspace-dialog-backdrop" role="presentation">
          <form className="workspace-create-dialog workspace-report-dialog" onSubmit={handleReportSubmit}>
            <header>
              <div>
                <span>Workspace feedback</span>
                <h2>Report issue or feature</h2>
              </div>
              <button
                aria-label="Close"
                onClick={() => {
                  setReportDialogOpen(false);
                  setReportError("");
                  setReportMessage("");
                }}
                type="button"
              >
                Close
              </button>
            </header>
            <div className="report-type-toggle" aria-label="Report type">
              <button
                className={reportType === "bug" ? "active" : ""}
                onClick={() => setReportType("bug")}
                type="button"
              >
                <BugReport fontSize="small" />
                Bug
              </button>
              <button
                className={reportType === "feature" ? "active" : ""}
                onClick={() => setReportType("feature")}
                type="button"
              >
                Feature
              </button>
            </div>
            <label>
              Title
              <input
                autoFocus
                onChange={(event) => setReportTitle(event.target.value)}
                placeholder={reportType === "bug" ? "What is broken?" : "What should be added?"}
                value={reportTitle}
              />
            </label>
            <label>
              Details
              <textarea
                onChange={(event) => setReportDescription(event.target.value)}
                placeholder="Explain what happened, expected behavior, steps, or why this feature matters."
                value={reportDescription}
              />
            </label>
            <p className="report-context-note">
              Saved to Nishant's Lyriclious Workspace with current page context.
            </p>
            {reportError ? <p>{reportError}</p> : null}
            {reportMessage ? <p className="workspace-dialog-success">{reportMessage}</p> : null}
            <div className="workspace-dialog-actions">
              <button
                disabled={reportSaving}
                onClick={() => {
                  setReportDialogOpen(false);
                  setReportError("");
                  setReportMessage("");
                }}
                type="button"
              >
                Cancel
              </button>
              <button disabled={reportSaving || !reportTitle.trim() || !reportDescription.trim()} type="submit">
                {reportSaving ? <WorkspaceInlineLoader compact label="Saving" /> : "Submit report"}
              </button>
            </div>
          </form>
        </div>
      ) : null}
      {logoutDialogOpen ? (
        <div className="workspace-dialog-backdrop" role="presentation">
          <section
            aria-labelledby="workspace-logout-title"
            aria-modal="true"
            className="workspace-create-dialog workspace-logout-dialog"
            role="dialog"
          >
            <header>
              <div>
                <span>Sign out</span>
                <h2 id="workspace-logout-title">Log out of Lyriclious?</h2>
              </div>
            </header>
            <p>You will be signed out of both Lyriclious Workspace and Lyriclious on this browser.</p>
            {logoutError ? <p className="workspace-logout-error" role="alert">{logoutError}</p> : null}
            <div className="workspace-dialog-actions">
              <button
                autoFocus
                disabled={logoutLoading}
                onClick={() => setLogoutDialogOpen(false)}
                type="button"
              >
                Stay signed in
              </button>
              <button
                className="workspace-logout-confirm"
                disabled={logoutLoading}
                onClick={confirmLogout}
                type="button"
              >
                {logoutLoading ? <WorkspaceInlineLoader compact label="Logging out" /> : "Log out"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

export default App;
